import React, { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from 'react';
import T from 'i18n';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  TextField,
  DialogContentText,
  Menu,
  Tooltip,
  MenuItem,
  IconButton,
} from '@mui/material';
import {
  CheckCircle,
  Folder,
  EditNoteOutlined,
  AttachFile,
  CloseRounded,
  AddRounded,
  EditOutlined,
  AutoAwesome,
  Undo,
  MoveUp,
} from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { useSelector } from 'react-redux';
import { useToasts } from 'react-toast-notifications';
import { selectCurrentCaseId } from 'common/selectors';
import api from 'common/api';
import FilesAndBundlesSelectionDialog from './FilesAndBundlesSelectionDialog';
import type { AllowedMode } from './FilesAndBundlesSelectionDialog';
import PromptPlaceholderInput from './PromptPlaceholderInput';
import { useUpdateUserSettings } from 'features/common/redux/updateUserSettings';
import { MAX_PROMPT_PLACEHOLDERS_ENTRIES } from './constants';
import { normalizeStandingInstructions } from 'features/case/utils/standingInstructions';

interface PromptItem {
  id?: string;
  name: string;
  prompt: string;
  category: string;
  categoryId?: string;
  outputType: string;
  filterType?: {
    documents: boolean;
    bundles: boolean;
    multipleDocuments: boolean;
    multipleBundles: boolean;
    isComparison: boolean;
    sticky?: boolean;
    anyFilesOrBundles?: boolean;
    [key: string]: any;
  };
}

interface PromptCategoryMap {
  [key: string]: PromptItem[] | undefined;
}

interface PromptLibraryResponse {
  defaultPrompts: PromptCategoryMap;
  customPrompts: PromptCategoryMap;
}

interface ImprovePromptResponse {
  originalPrompt?: string;
  improvedPrompt?: string;
  model?: string;
  goal?: string;
  error?: {
    message?: string;
  };
}

interface SubCategory {
  key: string;
  displayName: string;
  categoryId?: string;
  items: PromptItem[];
}

interface MovePromptState {
  id: string;
  name: string;
  prompt: string;
  sourceCategoryId: string;
  sourceCategoryName: string;
}

type StandingInstructionKey = 'context' | 'outputRules';

const moveUncategorizedCategoryToEnd = (
  categories: SubCategory[],
  uncategorizedName: string,
): SubCategory[] => {
  const normalizedUncategorizedName = String(uncategorizedName || '')
    .trim()
    .toLowerCase();

  if (!normalizedUncategorizedName) return categories;

  const orderedCategories: SubCategory[] = [];
  const uncategorizedCategories: SubCategory[] = [];

  categories.forEach((category) => {
    const normalizedCategoryName = String(category.displayName || category.key || '')
      .trim()
      .toLowerCase();

    if (normalizedCategoryName === normalizedUncategorizedName) {
      uncategorizedCategories.push(category);
      return;
    }

    orderedCategories.push(category);
  });

  return [...orderedCategories, ...uncategorizedCategories];
};

const getApiErrorMessage = (response: any, fallbackMessage: string) => {
  const error = response?.error;

  if (!error) return fallbackMessage;
  if (typeof error === 'string' && error.trim()) return error;
  if (typeof error?.message === 'string' && error.message.trim()) return error.message;
  if (typeof error?.detail === 'string' && error.detail.trim()) return error.detail;

  return fallbackMessage;
};

interface AiToolsDialogProps {
  open: boolean;
  onClose: () => void;
  /**
   * Legacy individual handlers (still supported for backward compatibility)
   */
  onSelect?: (prompt: PromptItem) => void;
  onFilesChange?: (files: any[], compare?: boolean) => void;
  /**
   * New combined handler: invoked when user clicks Save. Provides both prompt & file selections.
   */
  onSaveSelections?: (data: {
    prompt?: PromptItem | null;
    files?: any[];
    location?: any[]; // selected bundles
    compare?: boolean;
  }) => void;
  directAnswers?: boolean;
  filters: any;
  onCategoriesLoaded?: (categories: Array<{ id: string; name: string }>) => void;
  showStandingInstructionsTab?: boolean;
}

export default function AiToolsDialog({
  open,
  onClose,
  onSelect,
  onSaveSelections,
  filters,
  onFilesChange,
  onCategoriesLoaded,
  showStandingInstructionsTab = false,
}: AiToolsDialogProps) {
  const [mainTab, setMainTab] = useState<number>(0);
  const [selectedCategory, setSelectedCategory] = useState<number>(0);
  const [selectedCustomCategory, setSelectedCustomCategory] = useState<number>(0);
  const [promptLibrary, setPromptLibrary] = useState<SubCategory[]>([]);
  const [customPromptLibrary, setCustomPromptLibrary] = useState<SubCategory[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubCategory, setSelectedSubCategory] = useState<string | null>(null);
  // Local (pending) selected files for compare flow inside AI Tools without mutating global filters until user saves.
  const [pendingFiles, setPendingFiles] = useState<any[]>([]);
  const [pendingCompareMode, setPendingCompareMode] = useState<boolean>(false);
  // Local (pending) selected bundles
  const [pendingBundles, setPendingBundles] = useState<any[]>([]);
  const [filesAndBundlesDialogOpen, setFilesAndBundlesDialogOpen] = useState(false);
  const [filesAndBundlesMode, setFilesAndBundlesMode] = useState<AllowedMode>('both');
  const [filesAndBundlesCompareMode, setFilesAndBundlesCompareMode] = useState(false);
  // Local (pending) claimant, respondent, witness, expert, topic, party
  const [pendingClaimant, setPendingClaimant] = useState<string>('');
  const [pendingRespondent, setPendingRespondent] = useState<string>('');
  const [pendingWitness, setPendingWitness] = useState<string>('');
  const [pendingExpert, setPendingExpert] = useState<string>('');
  const [pendingTopic, setPendingTopic] = useState<string>('');
  const [pendingParty, setPendingParty] = useState<string>('');
  const [claimantAnchorEl, setClaimantAnchorEl] = useState<HTMLElement | null>(null);
  const [respondentAnchorEl, setRespondentAnchorEl] = useState<HTMLElement | null>(null);
  const [witnessAnchorEl, setWitnessAnchorEl] = useState<HTMLElement | null>(null);
  const [expertAnchorEl, setExpertAnchorEl] = useState<HTMLElement | null>(null);
  const [topicAnchorEl, setTopicAnchorEl] = useState<HTMLElement | null>(null);
  const [partyAnchorEl, setPartyAnchorEl] = useState<HTMLElement | null>(null);
  // Local (pending) selected prompt item
  const [pendingPrompt, setPendingPrompt] = useState<PromptItem | null>(null);
  // Track initial values (re-applied every open if filters exist)
  const [initialCategoryIndex, setInitialCategoryIndex] = useState<number | null>(null);
  const [initialPromptName, setInitialPromptName] = useState<string | null>(null);
  // Prompt creation menu state
  const [promptMenuAnchorEl, setPromptMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [newPromptName, setNewPromptName] = useState<string>('');
  const [newPromptText, setNewPromptText] = useState<string>('');
  const [newPromptOriginalText, setNewPromptOriginalText] = useState<string>('');
  const [isCreatingPrompt, setIsCreatingPrompt] = useState<boolean>(false);
  const [isImprovingNewPrompt, setIsImprovingNewPrompt] = useState<boolean>(false);
  // Category creation menu state
  const [categoryMenuAnchorEl, setCategoryMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [newCategoryName, setNewCategoryName] = useState<string>('');
  const [isCreatingCategory, setIsCreatingCategory] = useState<boolean>(false);
  // Category edit/delete state
  const [editCategoryMenuAnchorEl, setEditCategoryMenuAnchorEl] = useState<HTMLElement | null>(
    null,
  );
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [editCategoryName, setEditCategoryName] = useState<string>('');
  const [editCategoryOriginalName, setEditCategoryOriginalName] = useState<string>('');
  const [isUpdatingCategory, setIsUpdatingCategory] = useState<boolean>(false);
  const [deleteCategoryConfirm, setDeleteCategoryConfirm] = useState<{
    id: string;
    name: string;
  } | null>(null);
  // After saving, navigate to this category once customPromptLibrary refreshes
  const [navigateToCategory, setNavigateToCategory] = useState<string | null>(null);
  // Edit custom prompt state
  const [editingPromptId, setEditingPromptId] = useState<string | null>(null);
  const [editName, setEditName] = useState<string>('');
  const [editPromptText, setEditPromptText] = useState<string>('');
  const [editPromptOriginalText, setEditPromptOriginalText] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isImprovingEditPrompt, setIsImprovingEditPrompt] = useState<boolean>(false);
  const editBoxRef = useRef<HTMLDivElement>(null);
  const lastClosedEditPromptId = useRef<string | null>(null);
  const newPromptImproveRequestIdRef = useRef(0);
  const editPromptImproveRequestIdRef = useRef(0);
  const standingInstructionEditBoxRef = useRef<HTMLDivElement>(null);
  const lastClosedStandingInstructionRef = useRef<StandingInstructionKey | null>(null);
  // Delete confirmation dialog state
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null);
  const [movePromptAnchorEl, setMovePromptAnchorEl] = useState<HTMLElement | null>(null);
  const [movePromptItem, setMovePromptItem] = useState<MovePromptState | null>(null);
  const [movePromptCategoryId, setMovePromptCategoryId] = useState<string>('');
  const [isMovingPrompt, setIsMovingPrompt] = useState<boolean>(false);
  const [standingInstructionDrafts, setStandingInstructionDrafts] = useState<
    Record<StandingInstructionKey, string>
  >({
    context: '',
    outputRules: '',
  });
  const [editingStandingInstruction, setEditingStandingInstruction] =
    useState<StandingInstructionKey | null>(null);

  const caseId = useSelector(selectCurrentCaseId);
  const { addToast } = useToasts();

  const { userSettings, updateUserSettings, updateUserSettingsPending } = useUpdateUserSettings();
  const standingInstructions = useMemo(
    () => normalizeStandingInstructions(userSettings?.aiSettings?.standingInstructions),
    [userSettings?.aiSettings?.standingInstructions],
  );
  const buildPromptKey = useCallback((prompt: PromptItem | null) => {
    const id = (prompt?.id || '').trim();
    if (!id) return '';
    return id;
  }, []);

  // Transform a category map into SubCategory[]
  const parseCategoryMap = useCallback(
    (map: PromptCategoryMap, filterCompare: boolean): SubCategory[] =>
      Object.entries(map)
        .filter(([, items]) => Array.isArray(items) && items!.length > 0)
        .map(([key, items]) => ({
          key,
          displayName: (items as PromptItem[])[0]?.category || key,
          items: items as PromptItem[],
        }))
        .filter((category) => {
          if (!filterCompare) return true;
          // Filter out 'compare' category when location or files arrays have items
          // BUT keep it if isCompareMode is true
          if (
            category.displayName.toLowerCase() === 'compare' &&
            filters &&
            !filters.isCompareMode &&
            ((filters.location && filters.location.length > 0) ||
              (filters.files && filters.files.length > 0))
          ) {
            return false;
          }
          return true;
        }),
    [filters],
  );

  // Fetch prompt library data and categories
  const fetchPromptLibrary = useCallback(async () => {
    if (!caseId) return;

    setIsLoading(true);
    setError(null);

    try {
      const [response, categoriesRes] = await Promise.all([
        api.get(`/prompts`) as Promise<PromptLibraryResponse>,
        api.get(`/prompt-categories`) as Promise<any>,
      ]);

      if (response && typeof response === 'object') {
        const defaultMap = response.defaultPrompts || {};
        const customMap = response.customPrompts || {};

        setPromptLibrary(parseCategoryMap(defaultMap, true));

        const parsedCustom = parseCategoryMap(customMap, false);

        // Merge categories from API — add empty entries for categories with no prompts yet
        const fetchedCategories = Array.isArray(categoriesRes) ? categoriesRes : [];
        onCategoriesLoaded?.(
          fetchedCategories
            .map((category: any) => ({
              id: String(category?.id || '').trim(),
              name: String(category?.name || '').trim(),
            }))
            .filter((category) => category.id && category.name),
        );

        // Attach categoryId from categories API to parsed custom prompts
        const parsedCustomWithIds = parsedCustom.map((sub) => {
          const match = fetchedCategories.find(
            (cat: any) => cat.name.toLowerCase() === sub.displayName.toLowerCase(),
          );
          return { ...sub, categoryId: match?.id || sub.items[0]?.categoryId };
        });

        const customCategoriesByName = new Map(
          parsedCustomWithIds.map((category) => [category.displayName.toLowerCase(), category]),
        );
        const fetchedCategoryKeys = new Set(
          fetchedCategories.map((category: any) => String(category?.name || '').toLowerCase()),
        );
        const orderedCustomCategories: SubCategory[] = fetchedCategories.map((category: any) => {
          const categoryName = String(category?.name || '').trim();
          const existingCategory = customCategoriesByName.get(categoryName.toLowerCase());

          if (existingCategory) {
            return {
              ...existingCategory,
              categoryId: category.id || existingCategory.categoryId,
            };
          }

          return {
            key: categoryName,
            displayName: categoryName,
            categoryId: category.id,
            items: [],
          };
        });
        const unmatchedCustomCategories = parsedCustomWithIds.filter(
          (category) => !fetchedCategoryKeys.has(category.displayName.toLowerCase()),
        );

        setCustomPromptLibrary(
          moveUncategorizedCategoryToEnd(
            [...orderedCustomCategories, ...unmatchedCustomCategories],
            T.translate('generic.uncategorized') || 'Uncategorized',
          ),
        );
      } else {
        setError('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching prompt library:', err);
      setError('Failed to load AI Prompt Library. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [caseId, onCategoriesLoaded, parseCategoryMap]);

  // Fetch data when dialog opens
  useEffect(() => {
    if (open) {
      fetchPromptLibrary();
    }
  }, [open, fetchPromptLibrary]);

  // After saving a custom prompt, navigate to its category once the library refreshes
  useEffect(() => {
    if (!navigateToCategory || customPromptLibrary.length === 0) return;
    const idx = customPromptLibrary.findIndex(
      (cat) => cat.displayName.toLowerCase() === navigateToCategory.toLowerCase(),
    );
    if (idx >= 0) {
      setSelectedCustomCategory(idx);
      setSelectedSubCategory(null);
      setPendingPrompt(null);
      setNavigateToCategory(null);
    }
    // Don't clear navigateToCategory if not found yet — wait for the next refresh
  }, [navigateToCategory, customPromptLibrary]);

  const updateStandingInstructionDraft = useCallback(
    (type: StandingInstructionKey, value: string) => {
      setStandingInstructionDrafts((prev) => ({
        ...prev,
        [type]: value,
      }));
    },
    [],
  );

  const closeStandingInstructionMenu = useCallback(() => {
    setEditingStandingInstruction((prev) => {
      lastClosedStandingInstructionRef.current = prev;
      return null;
    });
  }, []);

  const getStandingInstructionValue = useCallback(
    (type: StandingInstructionKey) => standingInstructions?.[type] || '',
    [standingInstructions],
  );

  const handleOpenStandingInstructionMenu = useCallback(
    (type: StandingInstructionKey) => {
      const isSameInstruction = lastClosedStandingInstructionRef.current === type;
      setEditingStandingInstruction(type);
      if (!isSameInstruction) {
        setStandingInstructionDrafts((prev) => ({
          ...prev,
          [type]: getStandingInstructionValue(type),
        }));
      }
    },
    [getStandingInstructionValue],
  );

  const persistStandingInstructions = useCallback(
    async (nextStandingInstructionsInput: Partial<Record<StandingInstructionKey, string>>) => {
      const nextStandingInstructions = normalizeStandingInstructions({
        ...nextStandingInstructionsInput,
        enabled: true,
      });
      const nextAiSettings = {
        ...(userSettings?.aiSettings || {}),
      } as Record<string, any>;

      if (nextStandingInstructions) {
        nextAiSettings.standingInstructions = nextStandingInstructions;
      } else {
        delete nextAiSettings.standingInstructions;
      }

      await updateUserSettings({
        userSettings: {
          ...userSettings,
          aiSettings: nextAiSettings,
        },
      });
    },
    [updateUserSettings, userSettings],
  );

  const handleSaveStandingInstruction = useCallback(
    async (type: StandingInstructionKey) => {
      const nextValue = standingInstructionDrafts[type] || '';
      try {
        await persistStandingInstructions({
          ...(standingInstructions || {}),
          [type]: nextValue,
        });
        lastClosedStandingInstructionRef.current = null;
        closeStandingInstructionMenu();
      } catch (err) {
        console.error('Error updating standing instructions:', err);
      }
    },
    [
      closeStandingInstructionMenu,
      persistStandingInstructions,
      standingInstructionDrafts,
      standingInstructions,
    ],
  );

  const handleCancelStandingInstructionEdit = useCallback(
    (type: StandingInstructionKey) => {
      lastClosedStandingInstructionRef.current = null;
      updateStandingInstructionDraft(type, getStandingInstructionValue(type));
      setEditingStandingInstruction(null);
    },
    [getStandingInstructionValue, updateStandingInstructionDraft],
  );

  // Helper: fully reset all local state to a blank slate
  const resetAllState = useCallback(() => {
    setMainTab(0);
    setSelectedCategory(0);
    setSelectedCustomCategory(0);
    setSelectedSubCategory(null);
    setError(null);
    setPendingFiles([]);
    setPendingCompareMode(false);
    setPendingBundles([]);
    setFilesAndBundlesDialogOpen(false);
    setPendingClaimant('');
    setPendingRespondent('');
    setPendingWitness('');
    setPendingExpert('');
    setPendingTopic('');
    setPendingParty('');
    setClaimantAnchorEl(null);
    setRespondentAnchorEl(null);
    setWitnessAnchorEl(null);
    setExpertAnchorEl(null);
    setTopicAnchorEl(null);
    setPartyAnchorEl(null);
    setPendingPrompt(null);
    setInitialCategoryIndex(null);
    setInitialPromptName(null);
    setPromptMenuAnchorEl(null);
    setNewPromptName('');
    setNewPromptText('');
    setNewPromptOriginalText('');
    setIsCreatingPrompt(false);
    newPromptImproveRequestIdRef.current += 1;
    setIsImprovingNewPrompt(false);
    setEditCategoryMenuAnchorEl(null);
    setEditingCategoryId(null);
    setEditCategoryName('');
    setEditCategoryOriginalName('');
    setIsUpdatingCategory(false);
    setDeleteCategoryConfirm(null);
    setNavigateToCategory(null);
    setEditingPromptId(null);
    setEditName('');
    setEditPromptText('');
    setEditPromptOriginalText('');
    setIsUpdating(false);
    editPromptImproveRequestIdRef.current += 1;
    setIsImprovingEditPrompt(false);
    setDeleteConfirm(null);
    setMovePromptAnchorEl(null);
    setMovePromptItem(null);
    setMovePromptCategoryId('');
    setIsMovingPrompt(false);
    setStandingInstructionDrafts({
      context: '',
      outputRules: '',
    });
    setEditingStandingInstruction(null);
  }, []);

  // When dialog closes, clear everything so next open starts fresh
  useEffect(() => {
    if (!open) {
      resetAllState();
    }
  }, [open, resetAllState]);

  const handleSubCategoryClick = (item: PromptItem) => {
    setSelectedSubCategory(item.name);
    setPendingPrompt(item);
    if (item?.filterType?.sticky) {
      const promptKey = buildPromptKey(item);
      const stickyFiles = resolveStickyFiles(promptKey);
      setPendingFiles(stickyFiles);
      setPendingCompareMode(stickyFiles.length > 1);
    }
    // Don't call onSelect here - delegate to Save button
  };

  const handleClose = () => {
    onClose();
  };

  const handleFilesChange = (files: any[], compareMode: boolean) => {
    setPendingFiles(files);
    setPendingCompareMode(compareMode);
    if (pendingPrompt?.filterType?.anyFilesOrBundles && files.length > 0) {
      setPendingBundles([]);
    }
    if (pendingPrompt?.filterType?.sticky) {
      const promptKey = buildPromptKey(pendingPrompt);
      if (!promptKey) return;
      const sanitizedFiles = Array.isArray(files)
        ? files
            .filter((file: any) => file?.id && !file?.isPlaceholder)
            .map((file: any) => ({
              id: file?.id,
              name: file?.name,
            }))
        : [];
      if (!userSettings || !caseId) return;

      const existingArray = userSettings.aiSettings?.promptPlaceholders || [];
      const caseIndex = existingArray.findIndex((entry: any) => entry.caseId === caseId);

      let updatedArray;
      if (caseIndex >= 0) {
        updatedArray = [...existingArray];
        updatedArray[caseIndex] = {
          ...updatedArray[caseIndex],
          promptStickyFiles: {
            ...(updatedArray[caseIndex]?.promptStickyFiles || {}),
            [promptKey]: sanitizedFiles,
          },
        };
      } else {
        const newEntry = {
          caseId,
          promptStickyFiles: {
            [promptKey]: sanitizedFiles,
          },
        };
        if (existingArray.length >= MAX_PROMPT_PLACEHOLDERS_ENTRIES) {
          updatedArray = [...existingArray.slice(1), newEntry];
        } else {
          updatedArray = [...existingArray, newEntry];
        }
      }

      updateUserSettings({
        userSettings: {
          ...userSettings,
          aiSettings: {
            ...userSettings.aiSettings,
            promptPlaceholders: updatedArray,
          },
        },
      });
    }
  };

  const handleCategoryClick = (index: number) => {
    if (mainTab === 0) {
      setSelectedCategory(index);
    } else if (mainTab === 1) {
      setSelectedCustomCategory(index);
    }

    const lib = mainTab === 0 ? promptLibrary : customPromptLibrary;

    // Handle sub-category selection logic
    if (mainTab === 0 && initialCategoryIndex !== null && index === initialCategoryIndex) {
      if (initialPromptName) {
        const found = lib[index]?.items.find((itm) => itm.name === initialPromptName);
        setSelectedSubCategory(found ? found.name : null);
        setPendingPrompt(found || null);
      } else {
        setSelectedSubCategory(null);
        setPendingPrompt(null);
      }
    } else {
      setSelectedSubCategory(null);
      setPendingPrompt(null);
    }

    // Update placeholders based on the new category
    const newCategoryKey = lib[index]?.key;
    setPendingClaimant(resolvePlaceholderValue('claimant', newCategoryKey));
    setPendingRespondent(resolvePlaceholderValue('respondent', newCategoryKey));
    setPendingWitness(resolvePlaceholderValue('witness', newCategoryKey));
    setPendingExpert(resolvePlaceholderValue('expert', newCategoryKey));
    setPendingTopic(resolvePlaceholderValue('topic', newCategoryKey));
    setPendingParty(resolvePlaceholderValue('party', newCategoryKey));
  };

  const activeLibrary = useMemo(
    () => (mainTab === 0 ? promptLibrary : mainTab === 1 ? customPromptLibrary : []),
    [mainTab, promptLibrary, customPromptLibrary],
  );
  const activeCategoryIndex = mainTab === 0 ? selectedCategory : selectedCustomCategory;
  const currentSubCategories = activeLibrary[activeCategoryIndex]?.items || [];
  const activeCategory = activeLibrary[activeCategoryIndex];
  const currentCategoryDisplayName =
    activeLibrary[activeCategoryIndex]?.displayName?.toLowerCase() || '';
  const currentCategoryKey = activeLibrary[activeCategoryIndex]?.key || '';
  const improvePromptLabel = T.translate('generic.improvePrompt') || 'Enhance prompt';
  const movePromptTooltip =
    T.translate('generic.movePromptTooltip') || 'Move the prompt to a different category';
  const editPromptTooltip = T.translate('generic.editPromptTooltip') || 'Edit the prompt';
  const deletePromptTooltip = T.translate('generic.deletePromptTooltip') || 'Delete the prompt';
  const selectPromptMoveCategoryLabel =
    T.translate('generic.selectPromptMoveCategory') || 'Please select a category';
  const improvePromptButtonSx = {
    textTransform: 'none',
    borderRadius: '4px',
    border: '1px solid #c7d2fe',
    background: '#eef2ff',
    color: '#4567e5',
    fontSize: '0.9rem',
    fontWeight: 600,
    px: 1.25,
    py: 0.25,
    minHeight: '24px',
    lineHeight: 1.2,
    boxShadow: 'none',
    '&:hover': {
      background: '#e0e7ff',
      borderColor: '#a5b4fc',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      background: '#eef2ff',
      borderColor: '#dbe4ff',
      color: '#94a3b8',
    },
    '& .MuiButton-startIcon': {
      marginLeft: 0,
      marginRight: '6px',
    },
  } as const;
  const resetPromptButtonLabel = T.translate('generic.resetToOriginal') || 'Reset to original';
  const resetPromptButtonSx = {
    textTransform: 'none',
    borderRadius: '4px',
    border: '1px solid #dbe4ff',
    background: '#ffffff',
    color: '#64748b',
    fontSize: '0.85rem',
    fontWeight: 600,
    px: 1.125,
    py: 0.25,
    minHeight: '24px',
    lineHeight: 1.2,
    boxShadow: 'none',
    '&:hover': {
      background: '#f8fafc',
      borderColor: '#cbd5e1',
      boxShadow: 'none',
    },
  } as const;

  const requestImprovedPrompt = useCallback(
    async (prompt: string): Promise<{ improvedPrompt: string; originalPrompt: string } | null> => {
      setError(null);
      try {
        const res = (await api.post('/prompts/improve', {
          prompt: prompt.trim(),
        })) as ImprovePromptResponse;

        if (res?.error) {
          setError(res.error.message || 'Failed to improve prompt. Please try again.');
          return null;
        }

        const improvedPrompt = typeof res?.improvedPrompt === 'string' ? res.improvedPrompt : '';
        if (!improvedPrompt.trim()) {
          setError('Failed to improve prompt. Please try again.');
          return null;
        }

        const originalPrompt =
          typeof res?.originalPrompt === 'string' ? res.originalPrompt : prompt;

        return {
          improvedPrompt,
          originalPrompt,
        };
      } catch (err) {
        console.error('Error improving prompt:', err);
        setError('Failed to improve prompt. Please try again.');
        return null;
      }
    },
    [],
  );

  const handleImproveNewPrompt = useCallback(async () => {
    if (!newPromptText.trim()) return;

    const requestId = newPromptImproveRequestIdRef.current + 1;
    newPromptImproveRequestIdRef.current = requestId;
    setIsImprovingNewPrompt(true);

    try {
      const improvementResult = await requestImprovedPrompt(newPromptText);
      if (newPromptImproveRequestIdRef.current !== requestId || !improvementResult) return;
      setNewPromptText(improvementResult.improvedPrompt);
      setNewPromptOriginalText(improvementResult.originalPrompt);
    } finally {
      if (newPromptImproveRequestIdRef.current === requestId) {
        setIsImprovingNewPrompt(false);
      }
    }
  }, [newPromptText, requestImprovedPrompt]);

  const handleImproveEditPrompt = useCallback(async () => {
    if (!editPromptText.trim()) return;

    const requestId = editPromptImproveRequestIdRef.current + 1;
    editPromptImproveRequestIdRef.current = requestId;
    setIsImprovingEditPrompt(true);

    try {
      const improvementResult = await requestImprovedPrompt(editPromptText);
      if (editPromptImproveRequestIdRef.current !== requestId || !improvementResult) return;
      setEditPromptText(improvementResult.improvedPrompt);
      setEditPromptOriginalText(improvementResult.originalPrompt);
    } finally {
      if (editPromptImproveRequestIdRef.current === requestId) {
        setIsImprovingEditPrompt(false);
      }
    }
  }, [editPromptText, requestImprovedPrompt]);

  const handleResetNewPrompt = useCallback(() => {
    if (!newPromptOriginalText) return;
    setNewPromptText(newPromptOriginalText);
    setNewPromptOriginalText('');
  }, [newPromptOriginalText]);

  const handleResetEditPrompt = useCallback(() => {
    if (!editPromptOriginalText) return;
    setEditPromptText(editPromptOriginalText);
    setEditPromptOriginalText('');
  }, [editPromptOriginalText]);

  const clearMovePromptMenu = useCallback(() => {
    setMovePromptAnchorEl(null);
    setMovePromptItem(null);
    setMovePromptCategoryId('');
  }, []);

  const handleCloseMovePromptMenu = useCallback(() => {
    if (isMovingPrompt) return;
    clearMovePromptMenu();
  }, [clearMovePromptMenu, isMovingPrompt]);

  // Create prompt handler (inside a category)
  const handleCreatePrompt = useCallback(async () => {
    if (!newPromptName.trim() || newPromptName.length > 50) return;
    if (!newPromptText.trim() || newPromptText.length > 10000) return;
    const categoryId = activeLibrary[activeCategoryIndex]?.categoryId;
    if (!categoryId) return;
    setIsCreatingPrompt(true);
    try {
      const res: any = await api.post(`/prompt-categories/${categoryId}/prompts`, {
        name: newPromptName.trim(),
        prompt: newPromptText.trim(),
      });
      if (res && res.error) {
        setError(res.error?.message || 'Failed to create prompt.');
      } else {
        setNewPromptName('');
        setNewPromptText('');
        setNewPromptOriginalText('');
        setPromptMenuAnchorEl(null);
        fetchPromptLibrary();
        addToast(T.translate('generic.promptCreated'), {
          appearance: 'success',
          autoDismiss: true,
        });
      }
    } catch (err) {
      console.error('Error creating prompt:', err);
      setError('Failed to create prompt. Please try again.');
    } finally {
      setIsCreatingPrompt(false);
    }
  }, [
    newPromptName,
    newPromptText,
    activeLibrary,
    activeCategoryIndex,
    fetchPromptLibrary,
    addToast,
  ]);

  // Create category handler
  const handleCreateCategory = useCallback(async () => {
    if (!newCategoryName.trim() || newCategoryName.length > 50) return;
    setIsCreatingCategory(true);
    try {
      const res: any = await api.post('/prompt-categories', {
        name: newCategoryName.trim(),
      });
      if (res && res.error) {
        setError(res.error?.message || 'Failed to create category.');
      } else {
        const savedName = newCategoryName.trim();
        setNewCategoryName('');
        setCategoryMenuAnchorEl(null);
        setNavigateToCategory(savedName);
        fetchPromptLibrary();
        addToast(T.translate('generic.categoryCreated'), {
          appearance: 'success',
          autoDismiss: true,
        });
      }
    } catch (err) {
      console.error('Error creating category:', err);
      setError('Failed to create category. Please try again.');
    } finally {
      setIsCreatingCategory(false);
    }
  }, [newCategoryName, fetchPromptLibrary, addToast]);

  // Update category handler
  const handleUpdateCategory = useCallback(async () => {
    if (!editingCategoryId || !editCategoryName.trim() || editCategoryName.length > 50) return;
    if (editCategoryName.trim() === editCategoryOriginalName) return;
    setIsUpdatingCategory(true);
    try {
      const res: any = await api.put(`/prompt-categories/${editingCategoryId}`, {
        name: editCategoryName.trim(),
      });
      if (res && res.error) {
        setError(res.error?.message || 'Failed to update category.');
      } else {
        setEditCategoryMenuAnchorEl(null);
        setEditingCategoryId(null);
        setEditCategoryName('');
        setEditCategoryOriginalName('');
        setNavigateToCategory(editCategoryName.trim());
        fetchPromptLibrary();
        addToast(T.translate('generic.categoryUpdated'), {
          appearance: 'success',
          autoDismiss: true,
        });
      }
    } catch (err) {
      console.error('Error updating category:', err);
      setError('Failed to update category. Please try again.');
    } finally {
      setIsUpdatingCategory(false);
    }
  }, [editingCategoryId, editCategoryName, editCategoryOriginalName, fetchPromptLibrary, addToast]);

  // Delete category handler (called after confirmation)
  const handleConfirmDeleteCategory = useCallback(async () => {
    if (!deleteCategoryConfirm) return;
    const { id } = deleteCategoryConfirm;
    setDeleteCategoryConfirm(null);
    try {
      const res: any = await api.del(`/prompt-categories/${id}`);
      if (res && res.error) {
        setError(res.error?.message || 'Failed to delete category.');
      } else {
        setSelectedCustomCategory(0);
        fetchPromptLibrary();
        addToast(T.translate('generic.categoryDeleted'), {
          appearance: 'success',
          autoDismiss: true,
        });
      }
    } catch (err) {
      console.error('Error deleting category:', err);
      setError('Failed to delete category. Please try again.');
    }
  }, [deleteCategoryConfirm, fetchPromptLibrary, addToast]);

  // Start editing a custom prompt — restore draft if re-opening the same prompt
  const handleStartEdit = useCallback((item: PromptItem) => {
    const isSamePrompt = lastClosedEditPromptId.current === item.id;
    setEditingPromptId(item.id || null);
    if (!isSamePrompt) {
      setEditName(item.name);
      setEditPromptText(item.prompt);
      setEditPromptOriginalText('');
    }
  }, []);

  // Close editing (click outside) — retain typed text
  const handleCloseEdit = useCallback(() => {
    setEditingPromptId((prev) => {
      lastClosedEditPromptId.current = prev;
      return null;
    });
  }, []);

  // Click outside edit box closes it without clearing text
  useEffect(() => {
    if (!editingPromptId) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (editBoxRef.current && !editBoxRef.current.contains(e.target as Node)) {
        handleCloseEdit();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editingPromptId, handleCloseEdit]);

  // Reposition edit overlay so it stays within the dialog bounds
  useLayoutEffect(() => {
    const box = editBoxRef.current;
    if (!editingPromptId || !box) return;

    const container = box.closest('.MuiDialogContent-root') as HTMLElement | null;
    if (!container) return;

    const parentCard = box.offsetParent as HTMLElement | null;
    if (!parentCard) return;

    const containerRect = container.getBoundingClientRect();
    const parentRect = parentCard.getBoundingClientRect();
    const boxWidth = box.offsetWidth;

    // Default position: left:0 means box starts at parentCard's left edge.
    // Calculate the ideal left offset (relative to parentCard) so the box
    // stays within the container's horizontal bounds.
    let idealLeft = 0; // default: align to card's left edge

    const boxLeftAbsolute = parentRect.left + idealLeft;
    const boxRightAbsolute = boxLeftAbsolute + boxWidth;

    if (boxRightAbsolute > containerRect.right) {
      // Shift left to prevent right overflow, plus 25px padding from right edge
      idealLeft -= boxRightAbsolute - containerRect.right + 25;
    }

    const adjustedLeftAbsolute = parentRect.left + idealLeft;
    if (adjustedLeftAbsolute < containerRect.left) {
      // Shift right to prevent left overflow
      idealLeft += containerRect.left - adjustedLeftAbsolute;
    }

    box.style.left = `${idealLeft}px`;
    box.style.right = 'auto';
  }, [editingPromptId]);

  useEffect(() => {
    if (!editingStandingInstruction) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        standingInstructionEditBoxRef.current &&
        !standingInstructionEditBoxRef.current.contains(e.target as Node)
      ) {
        closeStandingInstructionMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [editingStandingInstruction, closeStandingInstructionMenu]);

  useLayoutEffect(() => {
    const box = standingInstructionEditBoxRef.current;
    if (!editingStandingInstruction || !box) return;

    const container = box.closest('.MuiDialogContent-root') as HTMLElement | null;
    if (!container) return;

    const parentCard = box.offsetParent as HTMLElement | null;
    if (!parentCard) return;

    const containerRect = container.getBoundingClientRect();
    const parentRect = parentCard.getBoundingClientRect();
    const boxWidth = box.offsetWidth;

    let idealLeft = 0;

    const boxLeftAbsolute = parentRect.left + idealLeft;
    const boxRightAbsolute = boxLeftAbsolute + boxWidth;

    if (boxRightAbsolute > containerRect.right) {
      idealLeft -= boxRightAbsolute - containerRect.right + 25;
    }

    const adjustedLeftAbsolute = parentRect.left + idealLeft;
    if (adjustedLeftAbsolute < containerRect.left) {
      idealLeft += containerRect.left - adjustedLeftAbsolute;
    }

    box.style.left = `${idealLeft}px`;
    box.style.right = 'auto';
  }, [editingStandingInstruction]);

  // Cancel editing — clear typed text
  const handleCancelEdit = useCallback(() => {
    editPromptImproveRequestIdRef.current += 1;
    setIsImprovingEditPrompt(false);
    lastClosedEditPromptId.current = null;
    setEditingPromptId(null);
    setEditName('');
    setEditPromptText('');
    setEditPromptOriginalText('');
  }, []);

  // Update custom prompt handler
  const handleUpdateCustomPrompt = useCallback(async () => {
    if (!editingPromptId || !editName.trim() || editName.length > 50) return;
    if (!editPromptText.trim() || editPromptText.length > 10000) return;
    const categoryId = activeLibrary[activeCategoryIndex]?.categoryId;
    if (!categoryId) return;
    setIsUpdating(true);
    try {
      const res: any = await api.put(
        `/prompt-categories/${categoryId}/prompts/${editingPromptId}`,
        {
          name: editName.trim(),
          prompt: editPromptText.trim(),
        },
      );
      if (res && res.error) {
        setError(res.error?.message || 'Failed to update custom prompt.');
      } else {
        lastClosedEditPromptId.current = null;
        setEditingPromptId(null);
        setEditName('');
        setEditPromptText('');
        setEditPromptOriginalText('');
        fetchPromptLibrary();
        addToast(T.translate('generic.customPromptUpdated'), {
          appearance: 'success',
          autoDismiss: true,
        });
      }
    } catch (err) {
      console.error('Error updating custom prompt:', err);
      setError('Failed to update custom prompt. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  }, [
    editingPromptId,
    editName,
    editPromptText,
    activeLibrary,
    activeCategoryIndex,
    fetchPromptLibrary,
    addToast,
  ]);

  const deleteCustomPrompt = useCallback(async (categoryId: string, promptId: string) => {
    const res: any = await api.del(`/prompt-categories/${categoryId}/prompts/${promptId}`);

    if (res?.error) {
      throw new Error(getApiErrorMessage(res, 'Failed to delete custom prompt.'));
    }
  }, []);

  const handleOpenMovePromptMenu = useCallback(
    (
      event: React.MouseEvent<HTMLElement>,
      item: PromptItem,
      sourceCategory: SubCategory | undefined,
    ) => {
      const promptId = String(item.id || '').trim();
      const sourceCategoryId = String(sourceCategory?.categoryId || '').trim();
      const sourceCategoryName = String(sourceCategory?.displayName || item.category || '').trim();

      if (!promptId || !sourceCategoryId || !sourceCategoryName) return;

      event.stopPropagation();
      setError(null);
      setMovePromptAnchorEl(event.currentTarget);
      setMovePromptItem({
        id: promptId,
        name: item.name,
        prompt: item.prompt,
        sourceCategoryId,
        sourceCategoryName,
      });
      setMovePromptCategoryId(sourceCategoryId);
    },
    [],
  );

  const handleMovePromptCategoryChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const targetCategoryId = String(event.target.value || '').trim();
      const currentMovePrompt = movePromptItem;

      setMovePromptCategoryId(targetCategoryId);

      if (!currentMovePrompt || !targetCategoryId) return;
      if (targetCategoryId === currentMovePrompt.sourceCategoryId) return;

      const targetCategory = customPromptLibrary.find(
        (category) => String(category.categoryId || '').trim() === targetCategoryId,
      );
      const targetCategoryName = String(targetCategory?.displayName || '').trim();

      if (!targetCategory?.categoryId || !targetCategoryName) {
        setMovePromptCategoryId(currentMovePrompt.sourceCategoryId);
        return;
      }

      setIsMovingPrompt(true);
      setError(null);

      try {
        const createResponse: any = await api.post(
          `/prompt-categories/${targetCategoryId}/prompts`,
          {
            name: currentMovePrompt.name,
            prompt: currentMovePrompt.prompt,
          },
        );

        if (createResponse?.error) {
          throw new Error(
            getApiErrorMessage(
              createResponse,
              T.translate('generic.customPromptMoveFailed') ||
                'Failed to move custom prompt. Please try again.',
            ),
          );
        }

        try {
          await deleteCustomPrompt(currentMovePrompt.sourceCategoryId, currentMovePrompt.id);
        } catch {
          clearMovePromptMenu();
          setNavigateToCategory(targetCategoryName);
          await fetchPromptLibrary();
          setError(
            T.translate('generic.customPromptMovePartialFailure', {
              category: targetCategoryName,
            }) ||
              `The prompt was added to "${targetCategoryName}" but could not be removed from its original category. It may now appear in both categories.`,
          );
          return;
        }

        clearMovePromptMenu();
        setNavigateToCategory(targetCategoryName);
        await fetchPromptLibrary();
        addToast(T.translate('generic.customPromptMoved') || 'Prompt moved successfully', {
          appearance: 'success',
          autoDismiss: true,
        });
      } catch (err) {
        console.error('Error moving custom prompt:', err);
        setMovePromptCategoryId(currentMovePrompt.sourceCategoryId);
        setError(
          err instanceof Error && err.message
            ? err.message
            : T.translate('generic.customPromptMoveFailed') ||
                'Failed to move custom prompt. Please try again.',
        );
      } finally {
        setIsMovingPrompt(false);
      }
    },
    [
      movePromptItem,
      customPromptLibrary,
      clearMovePromptMenu,
      deleteCustomPrompt,
      fetchPromptLibrary,
      addToast,
    ],
  );

  // Delete custom prompt handler (called after confirmation)
  const handleConfirmDelete = useCallback(async () => {
    if (!deleteConfirm) return;
    const { id } = deleteConfirm;
    const categoryId = activeLibrary[activeCategoryIndex]?.categoryId;
    if (!categoryId) return;
    setDeleteConfirm(null);
    try {
      await deleteCustomPrompt(categoryId, id);
      await fetchPromptLibrary();
      addToast(T.translate('generic.customPromptDeleted'), {
        appearance: 'success',
        autoDismiss: true,
      });
    } catch (err) {
      console.error('Error deleting custom prompt:', err);
      setError(
        err instanceof Error && err.message
          ? err.message
          : 'Failed to delete custom prompt. Please try again.',
      );
    }
  }, [
    deleteConfirm,
    activeLibrary,
    activeCategoryIndex,
    deleteCustomPrompt,
    fetchPromptLibrary,
    addToast,
  ]);

  // Helper to resolve placeholder value from category settings or global settings
  const resolvePlaceholderValue = useCallback(
    (
      partyType: 'claimant' | 'respondent' | 'witness' | 'expert' | 'topic' | 'party',
      categoryKey?: string,
    ) => {
      // 1. Check category specific settings
      if (categoryKey) {
        const catSettings = userSettings?.aiSettings?.[categoryKey];
        // Handle both string and object format
        const catValue = catSettings?.[partyType];
        if (typeof catValue === 'string') return catValue;
        if (catValue?.text) return catValue.text;
      }

      // 2. Check new promptPlaceholders array
      const arrayData = userSettings?.aiSettings?.promptPlaceholders;
      if (Array.isArray(arrayData)) {
        const caseEntry = arrayData.find((entry: any) => entry.caseId === caseId);
        if (caseEntry?.[partyType]?.text) {
          return caseEntry[partyType].text;
        }
      }

      // 3. Fallback to old format (only for claimant/respondent)
      const oldValue =
        partyType === 'claimant' || partyType === 'respondent'
          ? userSettings?.aiSettings?.[partyType]
          : undefined;
      if (typeof oldValue === 'string') {
        return oldValue;
      }
      if (oldValue?.caseId === caseId && oldValue?.text) {
        return oldValue.text;
      }

      return '';
    },
    [userSettings, caseId],
  );

  const resolveStickyFiles = useCallback(
    (promptKey?: string) => {
      if (!promptKey) return [];
      const arrayData = userSettings?.aiSettings?.promptPlaceholders;
      if (Array.isArray(arrayData)) {
        const caseEntry = arrayData.find((entry: any) => entry.caseId === caseId);
        const promptStickyFiles = caseEntry?.promptStickyFiles || {};
        const stored = promptStickyFiles?.[promptKey];
        if (Array.isArray(stored)) return stored;
      }
      return [];
    },
    [userSettings, caseId],
  );

  useEffect(() => {
    if (!open) return;
    if (!pendingPrompt?.filterType?.sticky) return;
    const promptKey = buildPromptKey(pendingPrompt);
    const stickyFiles = resolveStickyFiles(promptKey);
    setPendingFiles(stickyFiles);
    setPendingCompareMode(stickyFiles.length > 1);
  }, [open, pendingPrompt, resolveStickyFiles, buildPromptKey]);

  // Memoized prompt with placeholder replacements applied
  const promptWithReplacements = useMemo(() => {
    if (!pendingPrompt) return null;

    const ft = (pendingPrompt.filterType || {}) as any;
    const allowClaimant = !!ft.claimant;
    const allowRespondent = !!ft.respondent;
    const allowWitness = !!ft.witness;
    const allowExpert = !!ft.expert;
    const allowTopic = !!ft.topic;
    const allowParty = !!ft.party;

    // If prompt doesn't require any placeholders, return as-is
    if (
      !allowClaimant &&
      !allowRespondent &&
      !allowWitness &&
      !allowExpert &&
      !allowTopic &&
      !allowParty
    )
      return pendingPrompt;

    // Replace placeholders in prompt text
    let updatedPromptText = pendingPrompt.prompt;
    if (allowClaimant && pendingClaimant.trim()) {
      updatedPromptText = updatedPromptText.replace(/\{\{CLAIMANT\}\}/g, pendingClaimant);
    }
    if (allowRespondent && pendingRespondent.trim()) {
      updatedPromptText = updatedPromptText.replace(/\{\{RESPONDENT\}\}/g, pendingRespondent);
    }
    if (allowWitness && pendingWitness.trim()) {
      updatedPromptText = updatedPromptText.replace(/\{\{WITNESS\}\}/g, pendingWitness);
    }
    if (allowExpert && pendingExpert.trim()) {
      updatedPromptText = updatedPromptText.replace(/\{\{EXPERT\}\}/g, pendingExpert);
    }
    if (allowTopic && pendingTopic.trim()) {
      updatedPromptText = updatedPromptText.replace(/\{\{TOPIC\}\}/g, pendingTopic);
    }
    if (allowParty && pendingParty.trim()) {
      updatedPromptText = updatedPromptText.replace(/\{\{PARTY\}\}/g, pendingParty);
    }

    return { ...pendingPrompt, prompt: updatedPromptText };
  }, [
    pendingPrompt,
    pendingClaimant,
    pendingRespondent,
    pendingWitness,
    pendingExpert,
    pendingTopic,
    pendingParty,
  ]);

  // Helper: render plain text with "\n" as real <br /> lines
  const renderLinesWithBreaks = (text: string) => {
    const parts = (text || '').split('\n');
    return (
      <>
        {parts.map((line, idx) => (
          <React.Fragment key={idx}>
            {line}
            {idx < parts.length - 1 && <br />}
          </React.Fragment>
        ))}
      </>
    );
  };

  // Helper function to render prompt text with highlighted placeholder substitutions
  const renderPromptText = (promptText: string, isSelected: boolean) => {
    // Check if we have substitutions to make
    const hasClaimantPlaceholder = promptText.includes('{{CLAIMANT}}');
    const hasRespondentPlaceholder = promptText.includes('{{RESPONDENT}}');
    const hasWitnessPlaceholder = promptText.includes('{{WITNESS}}');
    const hasExpertPlaceholder = promptText.includes('{{EXPERT}}');
    const hasTopicPlaceholder = promptText.includes('{{TOPIC}}');
    const hasPartyPlaceholder = promptText.includes('{{PARTY}}');

    if (
      !isSelected &&
      !hasClaimantPlaceholder &&
      !hasRespondentPlaceholder &&
      !hasWitnessPlaceholder &&
      !hasExpertPlaceholder &&
      !hasTopicPlaceholder &&
      !hasPartyPlaceholder
    ) {
      return renderLinesWithBreaks(promptText);
    }

    // Split the text by placeholders and build React nodes with highlighted spans
    let remainingText = promptText;
    let keyCounter = 0;
    const nodes: React.ReactNode[] = [];

    while (remainingText.length > 0) {
      const claimantIndex = remainingText.indexOf('{{CLAIMANT}}');
      const respondentIndex = remainingText.indexOf('{{RESPONDENT}}');
      const witnessIndex = remainingText.indexOf('{{WITNESS}}');
      const expertIndex = remainingText.indexOf('{{EXPERT}}');
      const topicIndex = remainingText.indexOf('{{TOPIC}}');
      const partyIndex = remainingText.indexOf('{{PARTY}}');

      // Find the next placeholder
      let nextIndex = -1;
      let nextType: 'claimant' | 'respondent' | 'witness' | 'expert' | 'topic' | 'party' | null =
        null;

      const indices = [
        { type: 'claimant' as const, index: claimantIndex },
        { type: 'respondent' as const, index: respondentIndex },
        { type: 'witness' as const, index: witnessIndex },
        { type: 'expert' as const, index: expertIndex },
        { type: 'topic' as const, index: topicIndex },
        { type: 'party' as const, index: partyIndex },
      ].filter((entry) => entry.index !== -1);

      if (indices.length > 0) {
        const next = indices.reduce((min, curr) => (curr.index < min.index ? curr : min));
        nextIndex = next.index;
        nextType = next.type;
      }

      if (nextIndex === -1) {
        // No more placeholders
        nodes.push(remainingText);
        break;
      }

      // Add text before placeholder
      if (nextIndex > 0) {
        nodes.push(remainingText.substring(0, nextIndex));
      }

      // Add highlighted substitution as React elements
      if (nextType === 'claimant' && pendingClaimant.trim()) {
        nodes.push(
          <span key={`claimant-${keyCounter++}`} style={{ color: '#EF8E43', fontWeight: 'bold' }}>
            {pendingClaimant}
          </span>,
        );
        remainingText = remainingText.substring(nextIndex + '{{CLAIMANT}}'.length);
      } else if (nextType === 'respondent' && pendingRespondent.trim()) {
        nodes.push(
          <span key={`respondent-${keyCounter++}`} style={{ color: '#EF8E43', fontWeight: 'bold' }}>
            {pendingRespondent}
          </span>,
        );
        remainingText = remainingText.substring(nextIndex + '{{RESPONDENT}}'.length);
      } else if (nextType === 'witness' && pendingWitness.trim()) {
        nodes.push(
          <span key={`witness-${keyCounter++}`} style={{ color: '#EF8E43', fontWeight: 'bold' }}>
            {pendingWitness}
          </span>,
        );
        remainingText = remainingText.substring(nextIndex + '{{WITNESS}}'.length);
      } else if (nextType === 'expert' && pendingExpert.trim()) {
        nodes.push(
          <span key={`expert-${keyCounter++}`} style={{ color: '#EF8E43', fontWeight: 'bold' }}>
            {pendingExpert}
          </span>,
        );
        remainingText = remainingText.substring(nextIndex + '{{EXPERT}}'.length);
      } else if (nextType === 'topic' && pendingTopic.trim()) {
        nodes.push(
          <span key={`topic-${keyCounter++}`} style={{ color: '#EF8E43', fontWeight: 'bold' }}>
            {pendingTopic}
          </span>,
        );
        remainingText = remainingText.substring(nextIndex + '{{TOPIC}}'.length);
      } else if (nextType === 'party' && pendingParty.trim()) {
        nodes.push(
          <span key={`party-${keyCounter++}`} style={{ color: '#EF8E43', fontWeight: 'bold' }}>
            {pendingParty}
          </span>,
        );
        remainingText = remainingText.substring(nextIndex + '{{PARTY}}'.length);
      } else {
        // Placeholder found but no value entered yet - keep the placeholder
        const placeholderLength =
          nextType === 'claimant'
            ? '{{CLAIMANT}}'.length
            : nextType === 'respondent'
              ? '{{RESPONDENT}}'.length
              : nextType === 'witness'
                ? '{{WITNESS}}'.length
                : nextType === 'expert'
                  ? '{{EXPERT}}'.length
                  : nextType === 'topic'
                    ? '{{TOPIC}}'.length
                    : '{{PARTY}}'.length;
        nodes.push(remainingText.substring(nextIndex, nextIndex + placeholderLength));
        remainingText = remainingText.substring(nextIndex + placeholderLength);
      }
    }

    // Now we have a flat array of strings + highlighted spans; apply line breaks
    const combinedTextAndNodes = nodes;
    const result: React.ReactNode[] = [];

    combinedTextAndNodes.forEach((node, idx) => {
      if (typeof node === 'string') {
        const parts = node.split('\n');
        parts.forEach((part, pIdx) => {
          if (part) {
            result.push(part);
          }
          if (pIdx < parts.length - 1) {
            result.push(<br key={`br-${idx}-${pIdx}`} />);
          }
        });
      } else {
        result.push(node);
      }
    });

    return <>{result}</>;
  };

  // Separate useEffect to update placeholders when userSettings changes (without resetting category)
  useEffect(() => {
    if (!open) return;

    // Only update if filters don't already have these values (avoid overwriting filter values)
    if (!filters?.claimant) {
      setPendingClaimant(resolvePlaceholderValue('claimant', currentCategoryKey));
    }
    if (!filters?.respondent) {
      setPendingRespondent(resolvePlaceholderValue('respondent', currentCategoryKey));
    }
    if (!filters?.witness) {
      setPendingWitness(resolvePlaceholderValue('witness', currentCategoryKey));
    }
    if (!filters?.expert) {
      setPendingExpert(resolvePlaceholderValue('expert', currentCategoryKey));
    }
    if (!filters?.topic) {
      setPendingTopic(resolvePlaceholderValue('topic', currentCategoryKey));
    }
    if (!filters?.party) {
      setPendingParty(resolvePlaceholderValue('party', currentCategoryKey));
    }
  }, [
    open,
    caseId,
    userSettings?.aiSettings,
    filters?.claimant,
    filters?.respondent,
    filters?.witness,
    filters?.expert,
    filters?.topic,
    filters?.party,
    currentCategoryKey,
    resolvePlaceholderValue,
  ]);

  // Each time dialog opens & prompt library is available, initialize from filters (if any)
  useEffect(() => {
    if (!open) return;
    if (!promptLibrary.length) return; // wait until data loaded

    // Initialize compare state from filters
    // When in compare mode, use compareFiles; otherwise use files
    const isCompareMode = !!filters?.isCompareMode;
    const filterFiles = isCompareMode
      ? Array.isArray(filters?.compareFiles)
        ? filters.compareFiles
        : []
      : Array.isArray(filters?.files)
        ? filters.files
        : [];
    // Initialize bundles from filters.location if available
    const filterLocations = Array.isArray(filters?.location) ? filters.location : [];
    setPendingBundles(filterLocations);

    // Resolve category & prompt from filters (if provided)
    const targetCategory = String(filters?.promptCategory || '')
      .trim()
      .toLowerCase();
    const targetPromptName = String(filters?.promptName || '').trim();

    let resolvedCategoryIndex: number | null = null;
    let resolvedPrompt: PromptItem | null = null;

    if (targetCategory) {
      const idx = promptLibrary.findIndex(
        (cat) => cat.displayName?.toLowerCase() === targetCategory,
      );
      if (idx >= 0) {
        resolvedCategoryIndex = idx;
      }
    }

    if (resolvedCategoryIndex !== null && targetPromptName) {
      const cat = promptLibrary[resolvedCategoryIndex];
      const found = cat.items.find(
        (itm) => itm.name.toLowerCase() === targetPromptName.toLowerCase(),
      );
      if (found) {
        resolvedPrompt = found;
      }
    }

    // Determine the category key to use for resolving placeholders
    let resolvedCategoryKey = currentCategoryKey;
    if (resolvedCategoryIndex !== null) {
      resolvedCategoryKey = promptLibrary[resolvedCategoryIndex]?.key || '';
    } else if (promptLibrary.length > 0) {
      // If no category resolved from filters, we default to index 0 (as per below logic)
      resolvedCategoryKey = promptLibrary[0]?.key || '';
    }

    // Initialize placeholders from filters if available, or fall back to saved userSettings
    setPendingClaimant(
      filters?.claimant || resolvePlaceholderValue('claimant', resolvedCategoryKey),
    );
    setPendingRespondent(
      filters?.respondent || resolvePlaceholderValue('respondent', resolvedCategoryKey),
    );
    setPendingWitness(filters?.witness || resolvePlaceholderValue('witness', resolvedCategoryKey));
    setPendingExpert(filters?.expert || resolvePlaceholderValue('expert', resolvedCategoryKey));
    setPendingTopic(filters?.topic || resolvePlaceholderValue('topic', resolvedCategoryKey));
    setPendingParty(filters?.party || resolvePlaceholderValue('party', resolvedCategoryKey));

    // Apply resolved state (prompt only if both category & prompt found)
    if (resolvedCategoryIndex !== null) {
      setSelectedCategory(resolvedCategoryIndex);
      if (resolvedPrompt) {
        setSelectedSubCategory(resolvedPrompt.name);
        setPendingPrompt(resolvedPrompt);
        setInitialCategoryIndex(resolvedCategoryIndex);
        setInitialPromptName(resolvedPrompt.name);
      } else {
        setSelectedSubCategory(null);
        setPendingPrompt(null); // prompt not found or not provided
        setInitialCategoryIndex(resolvedCategoryIndex);
        setInitialPromptName(null);
      }
    } else {
      // No category/prompt in filters -> blank selection
      setSelectedCategory(0);
      setSelectedSubCategory(null);
      setPendingPrompt(null);
      setInitialCategoryIndex(null);
      setInitialPromptName(null);
    }

    const shouldUseSticky = !!resolvedPrompt?.filterType?.sticky;
    const promptKey = shouldUseSticky ? buildPromptKey(resolvedPrompt) : '';
    const stickyFiles = shouldUseSticky ? resolveStickyFiles(promptKey) : [];
    const nextFiles = shouldUseSticky ? stickyFiles : filterFiles;
    setPendingFiles(nextFiles);
    setPendingCompareMode(shouldUseSticky ? nextFiles.length > 1 : isCompareMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    promptLibrary,
    filters?.files,
    filters?.compareFiles,
    filters?.isCompareMode,
    filters?.promptCategory,
    filters?.promptName,
    filters?.location,
    filters?.claimant,
    filters?.respondent,
    filters?.witness,
    filters?.expert,
    filters?.topic,
    filters?.party,
  ]);

  const standingInstructionConfig: Record<
    StandingInstructionKey,
    { label: string; placeholder: string; addLabel: string }
  > = {
    context: {
      label: T.translate('generic.context') || 'Context',
      placeholder:
        (T.translate('generic.standingInstructionsContextPlaceholder') as string) ||
        'Prepended to every prompt. Intended for case background, jurisdiction, parties, user role, etc.',
      addLabel: T.translate('generic.addContext') || '+ Add Context',
    },
    outputRules: {
      label: T.translate('generic.outputRules') || 'Output Rules',
      placeholder:
        (T.translate('generic.standingInstructionsOutputRulesPlaceholder') as string) ||
        'Appended to every prompt. Intended for formatting preferences, length constraints, tone, citation style, etc.',
      addLabel: T.translate('generic.addOutputRules') || '+ Add Output Rules',
    },
  };

  const isStandingInstructionSaveDisabled = useCallback(
    (type: StandingInstructionKey) => {
      if (updateUserSettingsPending) return true;
      const draft = (standingInstructionDrafts[type] || '').trim();
      const original = getStandingInstructionValue(type).trim();
      return draft === original;
    },
    [getStandingInstructionValue, standingInstructionDrafts, updateUserSettingsPending],
  );

  const handleStandingInstructionKeyDown = useCallback(
    (event: React.KeyboardEvent, disabled: boolean, onSubmit: () => void | Promise<void>) => {
      event.stopPropagation();
      if (event.key === 'Enter' && !event.shiftKey && !disabled) {
        event.preventDefault();
        onSubmit();
      }
    },
    [],
  );

  const renderStandingInstructionField = (
    type: StandingInstructionKey,
    options?: {
      autoFocus?: boolean;
      width?: number | string;
      mb?: number;
      onSubmit?: () => void | Promise<void>;
      submitDisabled?: boolean;
      multilineInputHeight?: string;
    },
  ) => {
    const config = standingInstructionConfig[type];
    const draftValue = standingInstructionDrafts[type] || '';

    return (
      <Box
        sx={{
          width: options?.width || '100%',
          maxWidth: '100%',
          mb: options?.mb ?? 0,
        }}
      >
        <TextField
          fullWidth
          multiline
          minRows={4}
          maxRows={4}
          label={config.label}
          value={draftValue}
          onChange={(event) => updateStandingInstructionDraft(type, event.target.value)}
          placeholder={config.placeholder}
          autoFocus={options?.autoFocus}
          onKeyDown={(event) =>
            handleStandingInstructionKeyDown(
              event,
              options?.submitDisabled ?? true,
              options?.onSubmit || (() => {}),
            )
          }
          InputLabelProps={{ shrink: true }}
          sx={{
            mb: 1,
            '& .MuiOutlinedInput-root': {
              fontSize: '12px',
              borderRadius: '5px',
              backgroundColor: '#ffffff',
              alignItems: 'flex-start',
            },
            ...(options?.multilineInputHeight
              ? {
                  '& .MuiInputBase-inputMultiline': {
                    height: options.multilineInputHeight,
                    overflow: 'auto !important',
                  },
                }
              : {}),
            '& .MuiInputLabel-root': {
              fontSize: '12px',
              fontWeight: 600,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#d1d5db',
              borderWidth: '1px',
            },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#999999',
              borderWidth: '1px',
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#999999',
              borderWidth: '1px',
            },
          }}
        />
      </Box>
    );
  };

  const renderStandingInstructionEditor = (type: StandingInstructionKey) => {
    return (
      <Box
        ref={standingInstructionEditBoxRef}
        onClick={(event) => event.stopPropagation()}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 10,
          width: 1024,
          padding: 2,
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
        }}
      >
        {renderStandingInstructionField(type, {
          autoFocus: true,
          onSubmit: () => handleSaveStandingInstruction(type),
          submitDisabled: isStandingInstructionSaveDisabled(type),
          multilineInputHeight: 'calc(85vh * 0.6 - 100px) !important',
        })}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button size="small" onClick={() => handleCancelStandingInstructionEdit(type)}>
            {T.translate('generic.cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={isStandingInstructionSaveDisabled(type)}
            onClick={() => handleSaveStandingInstruction(type)}
          >
            {T.translate('generic.save')}
          </Button>
        </Box>
      </Box>
    );
  };
  const isStandingInstructionsView = showStandingInstructionsTab && mainTab === 2;

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            height: '85vh',
            width: '100rem',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <DialogTitle
          sx={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#ffffff', pb: 1.5 }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#0f172a', fontSize: '18px' }}>
            {T.translate('generic.promptLibraryAiToolsHeader')}
          </Typography>
        </DialogTitle>

        <DialogContent
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            padding: 0,
            overflow: 'hidden',
          }}
        >
          <>
            {/* Main Category Tabs */}
            <Box sx={{ borderBottom: '1px solid #e5e7eb', backgroundColor: '#f8fafc' }}>
              <Tabs
                value={mainTab}
                onChange={(_e, newValue) => setMainTab(newValue)}
                TabIndicatorProps={{
                  sx: {
                    height: 3,
                    borderRadius: '3px 3px 0 0',
                    backgroundColor: '#4567e5',
                  },
                }}
                sx={{
                  minHeight: '48px',
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontSize: '13px',
                    fontWeight: 600,
                    minHeight: '48px',
                    color: '#64748b',
                    letterSpacing: '0.01em',
                    transition: 'color 160ms ease, background-color 160ms ease',
                    borderRadius: '8px 8px 0 0',
                    '&:hover': {
                      color: '#334155',
                      backgroundColor: '#f1f5f9',
                    },
                  },
                  '& .Mui-selected': {
                    color: '#4567e5 !important',
                  },
                }}
              >
                <Tab label={T.translate('generic.aiPromptLibrary')} />
                <Tab label={T.translate('generic.customAIPrompts')} />
                {showStandingInstructionsTab && (
                  <Tab label={T.translate('generic.standingInstructions')} />
                )}
              </Tabs>
            </Box>

            {/* Tab Content */}
            {mainTab === 0 && (
              <>
                {isLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flex: 1,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : error ? (
                  <Box sx={{ padding: 2 }}>
                    <Alert severity="error">{error}</Alert>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                    {/* Sub-category Chips */}
                    <Box
                      sx={{
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: '#f8fafc',
                        padding: '10px 16px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                      }}
                    >
                      {promptLibrary
                        .filter((category) => {
                          // Filter out 'compare' category when location or files arrays have items
                          // BUT keep it if isCompareMode is true
                          if (
                            category.displayName.toLowerCase() === 'compare' &&
                            filters &&
                            !filters.isCompareMode &&
                            ((filters.location && filters.location.length > 0) ||
                              (filters.files && filters.files.length > 0))
                          ) {
                            return false;
                          }
                          return true;
                        })
                        .map((category, index) => (
                          <Box
                            key={category.key}
                            onClick={() => handleCategoryClick(index)}
                            sx={{
                              padding: '6px 14px',
                              cursor: 'pointer',
                              borderRadius: '5px',
                              fontSize: '12px',
                              fontWeight: 600,
                              color: selectedCategory === index ? '#000' : '#0f172a',
                              backgroundColor: selectedCategory === index ? '#d3dcf8' : '#f8fafc',
                              border:
                                selectedCategory === index
                                  ? '1px solid #b8c5f2'
                                  : '1px solid #e5e7eb',
                              transition:
                                'background-color 160ms ease, border-color 160ms ease, color 160ms ease, transform 120ms ease',
                              '&:hover': {
                                backgroundColor: selectedCategory === index ? '#d3dcf8' : '#e8ecfb',
                                borderColor: selectedCategory === index ? '#b8c5f2' : '#c7d2fe',
                                color: '#000',
                              },
                              '&:active': {
                                transform: 'scale(0.98)',
                              },
                            }}
                          >
                            {category.displayName}
                          </Box>
                        ))}
                    </Box>

                    {/* Items List */}
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 0,
                        overflowY: 'auto',
                        padding: 2,
                        backgroundColor: '#f8fafc',
                        '&::-webkit-scrollbar': {
                          width: '4px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: 'rgba(0,0,0,0.18)',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                          background: 'rgba(0,0,0,0.3)',
                        },
                        scrollbarWidth: 'thin',
                        scrollbarColor: 'rgba(0,0,0,0.18) transparent',
                      }}
                    >
                      {currentSubCategories.length > 0 ? (
                        <Box
                          sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
                            gap: 2,
                          }}
                        >
                          {currentSubCategories.map((item, index) => (
                            <Box
                              key={index}
                              onClick={() => handleSubCategoryClick(item)}
                              sx={{
                                padding: 2,
                                border:
                                  selectedSubCategory === item.name
                                    ? '1px solid #c7d2fe'
                                    : '1px solid #e5e7eb',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                transition:
                                  'background-color 160ms ease, border-color 160ms ease, transform 120ms ease, box-shadow 160ms ease',
                                position: 'relative',
                                backgroundColor:
                                  selectedSubCategory === item.name ? '#eef2ff' : '#ffffff',
                                '&:hover': {
                                  backgroundColor:
                                    selectedSubCategory === item.name ? '#eef2ff' : '#f1f5f9',
                                  borderColor:
                                    selectedSubCategory === item.name ? '#c7d2fe' : '#cbd5e1',
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 12px rgba(69, 103, 229, 0.08)',
                                },
                                '&:active': {
                                  transform: 'translateY(0)',
                                },
                              }}
                            >
                              {selectedSubCategory === item.name && (
                                <CheckCircle
                                  fontSize="small"
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    color: '#4567e5',
                                  }}
                                />
                              )}
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  marginBottom: 1,
                                  color: selectedSubCategory === item.name ? '#4567e5' : '#0f172a',
                                }}
                              >
                                {item.name}
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  color: '#64748b',
                                  fontSize: '12px',
                                  lineHeight: 1.5,
                                }}
                              >
                                {renderPromptText(item.prompt, selectedSubCategory === item.name)}
                              </Typography>
                              {/** Inline Files and Bundles selection chips in the same row */}
                              <Box
                                mt={2}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'row',
                                  gap: 1,
                                  flexWrap: 'wrap',
                                }}
                              >
                                {(() => {
                                  const isSelected =
                                    pendingPrompt && pendingPrompt.name === item.name;
                                  if (!isSelected) return null;
                                  const isAnyFilesOrBundles =
                                    !!pendingPrompt?.filterType?.anyFilesOrBundles;
                                  const needsFilesOnly =
                                    !isAnyFilesOrBundles &&
                                    !!(
                                      pendingPrompt?.filterType?.documents ||
                                      pendingPrompt?.filterType?.multipleDocuments ||
                                      pendingPrompt?.filterType?.sticky
                                    );
                                  const needsBundlesOnly =
                                    !isAnyFilesOrBundles &&
                                    !!(
                                      pendingPrompt?.filterType?.bundles ||
                                      pendingPrompt?.filterType?.multipleBundles
                                    );
                                  const hasPendingItems =
                                    pendingFiles.length > 0 || pendingBundles.length > 0;
                                  const hasSelection = hasPendingItems;

                                  // Determine mode and label
                                  let chipMode: 'files' | 'bundles' | 'both' = 'both';
                                  let chipIcon = <AttachFile fontSize="small" />;
                                  let chipLabel: string;
                                  let onClear: () => void;

                                  if (isAnyFilesOrBundles) {
                                    chipMode = 'both';
                                    if (pendingFiles.length > 0) {
                                      chipIcon = <AttachFile fontSize="small" />;
                                      const base = (pendingFiles[0]?.name || '').substring(0, 35);
                                      const suffix =
                                        pendingFiles.length > 1
                                          ? ` & ${pendingFiles.length - 1} more ...`
                                          : '';
                                      chipLabel = `${base}${suffix}`;
                                    } else if (pendingBundles.length > 0) {
                                      chipIcon = <Folder fontSize="small" />;
                                      const base = (pendingBundles[0]?.name || '').substring(0, 35);
                                      const suffix =
                                        pendingBundles.length > 1
                                          ? ` & ${pendingBundles.length - 1} more ...`
                                          : '';
                                      chipLabel = `${base}${suffix}`;
                                    } else {
                                      chipLabel =
                                        (T.translate('case.selectBundlesOrDocuments') as string) ||
                                        'Select Sources';
                                    }
                                    onClear = () => {
                                      handleFilesChange([], false);
                                      setPendingBundles([]);
                                    };
                                  } else if (needsFilesOnly) {
                                    chipMode = 'files';
                                    chipIcon = <AttachFile fontSize="small" />;
                                    if (pendingFiles.length > 0) {
                                      const base = (pendingFiles[0]?.name || '').substring(0, 35);
                                      const suffix =
                                        pendingFiles.length > 1
                                          ? ` & ${pendingFiles.length - 1} more ...`
                                          : '';
                                      chipLabel = `${base}${suffix}`;
                                    } else {
                                      chipLabel =
                                        (T.translate('generic.selectFiles') as string) ||
                                        'Select Files';
                                    }
                                    onClear = () => handleFilesChange([], false);
                                  } else if (needsBundlesOnly) {
                                    chipMode = 'bundles';
                                    chipIcon = <Folder fontSize="small" />;
                                    if (pendingBundles.length > 0) {
                                      const base = (pendingBundles[0]?.name || '').substring(0, 35);
                                      const suffix =
                                        pendingBundles.length > 1
                                          ? ` & ${pendingBundles.length - 1} more ...`
                                          : '';
                                      chipLabel = `${base}${suffix}`;
                                    } else {
                                      chipLabel =
                                        (T.translate('generic.selectBundles') as string) ||
                                        'Select Bundles';
                                    }
                                    onClear = () => setPendingBundles([]);
                                  } else {
                                    return null;
                                  }

                                  const isComparisonPrompt =
                                    !!pendingPrompt?.filterType?.isComparison;

                                  return (
                                    <Box
                                      onClick={(e) => e.stopPropagation()}
                                      sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}
                                    >
                                      {/* Compare chip for isComparison prompts */}
                                      {isComparisonPrompt &&
                                        (() => {
                                          const hasCompareFiles =
                                            pendingCompareMode && pendingFiles.length > 0;
                                          const compareLabel = hasCompareFiles
                                            ? (() => {
                                                const base = (
                                                  pendingFiles[0]?.name || ''
                                                ).substring(0, 35);
                                                const suffix =
                                                  pendingFiles.length > 1
                                                    ? ` & ${pendingFiles.length - 1} more ...`
                                                    : '';
                                                return `${base}${suffix}`;
                                              })()
                                            : (T.translate(
                                                'generic.selectFilesToCompare',
                                              ) as string) || 'Select Files to Compare';
                                          return (
                                            <Chip
                                              size="small"
                                              icon={
                                                <span className="sticky-chip-icon">
                                                  <span
                                                    className="sticky-chip-iconDefault"
                                                    aria-hidden="true"
                                                  >
                                                    <AttachFile fontSize="small" />
                                                  </span>
                                                  {hasCompareFiles && (
                                                    <span
                                                      className="sticky-chip-iconHover"
                                                      aria-hidden="true"
                                                      onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleFilesChange([], false);
                                                        setPendingCompareMode(false);
                                                      }}
                                                    >
                                                      <CloseRounded fontSize="small" />
                                                    </span>
                                                  )}
                                                </span>
                                              }
                                              label={compareLabel}
                                              clickable
                                              sx={{
                                                backgroundColor: '#f8fafc',
                                                border: '1px solid #e5e7eb',
                                                color: '#0f172a',
                                                fontWeight: 600,
                                                fontSize: '0.85rem',
                                                borderRadius: '16px',
                                                padding: '5px 10px 5px 5px',
                                                height: 'auto',
                                                '& .MuiChip-label': {
                                                  padding: 0,
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                  whiteSpace: 'nowrap',
                                                },
                                                '& .MuiChip-icon': {
                                                  color: '#0f172a',
                                                  marginLeft: '4px',
                                                  marginRight: '6px',
                                                },
                                                '& .sticky-chip-icon': {
                                                  position: 'relative',
                                                  width: '18px',
                                                  height: '18px',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                },
                                                '& .sticky-chip-iconDefault': {
                                                  position: 'absolute',
                                                  inset: 0,
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                },
                                                '& .sticky-chip-iconHover': {
                                                  position: 'absolute',
                                                  inset: 0,
                                                  display: 'none',
                                                  alignItems: 'center',
                                                  justifyContent: 'center',
                                                  width: '18px',
                                                  height: '18px',
                                                  borderRadius: '999px',
                                                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                  border: '1px solid rgba(0, 0, 0, 0.18)',
                                                  color: '#111827',
                                                  cursor: 'pointer',
                                                },
                                                ...(hasCompareFiles
                                                  ? {
                                                      '&:hover .sticky-chip-iconDefault': {
                                                        display: 'none',
                                                      },
                                                      '&:hover .sticky-chip-iconHover': {
                                                        display: 'inline-flex',
                                                      },
                                                    }
                                                  : {}),
                                                '&:hover': {
                                                  backgroundColor: '#eef2ff',
                                                  borderColor: '#cbd5e1',
                                                  color: '#0f172a',
                                                  '& .MuiChip-icon': {
                                                    color: '#0f172a',
                                                  },
                                                },
                                              }}
                                              onClick={() => {
                                                setFilesAndBundlesMode('files');
                                                setFilesAndBundlesCompareMode(true);
                                                setFilesAndBundlesDialogOpen(true);
                                              }}
                                            />
                                          );
                                        })()}
                                      {/* Standard files/bundles chip (skip for comparison-only prompts) */}
                                      {!isComparisonPrompt && (
                                        <Chip
                                          size="small"
                                          icon={
                                            <span className="sticky-chip-icon">
                                              <span
                                                className="sticky-chip-iconDefault"
                                                aria-hidden="true"
                                              >
                                                {chipIcon}
                                              </span>
                                              {hasSelection && (
                                                <span
                                                  className="sticky-chip-iconHover"
                                                  aria-hidden="true"
                                                  onClick={(event) => {
                                                    event.stopPropagation();
                                                    onClear();
                                                  }}
                                                >
                                                  <CloseRounded fontSize="small" />
                                                </span>
                                              )}
                                            </span>
                                          }
                                          label={chipLabel}
                                          clickable
                                          sx={{
                                            backgroundColor: '#f8fafc',
                                            border: '1px solid #e5e7eb',
                                            color: '#0f172a',
                                            fontWeight: 600,
                                            fontSize: '0.85rem',
                                            borderRadius: '16px',
                                            padding: '5px 10px 5px 5px',
                                            height: 'auto',
                                            '& .MuiChip-label': {
                                              padding: 0,
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                            },
                                            '& .MuiChip-icon': {
                                              color: '#0f172a',
                                              marginLeft: '4px',
                                              marginRight: '6px',
                                            },
                                            '& .sticky-chip-icon': {
                                              position: 'relative',
                                              width: '18px',
                                              height: '18px',
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                            },
                                            '& .sticky-chip-iconDefault': {
                                              position: 'absolute',
                                              inset: 0,
                                              display: 'inline-flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                            },
                                            '& .sticky-chip-iconHover': {
                                              position: 'absolute',
                                              inset: 0,
                                              display: 'none',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              width: '18px',
                                              height: '18px',
                                              borderRadius: '999px',
                                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                              border: '1px solid rgba(0, 0, 0, 0.18)',
                                              color: '#111827',
                                              cursor: 'pointer',
                                            },
                                            ...(hasSelection
                                              ? {
                                                  '&:hover .sticky-chip-iconDefault': {
                                                    display: 'none',
                                                  },
                                                  '&:hover .sticky-chip-iconHover': {
                                                    display: 'inline-flex',
                                                  },
                                                }
                                              : {}),
                                            '&:hover': {
                                              backgroundColor: '#eef2ff',
                                              borderColor: '#cbd5e1',
                                              color: '#0f172a',
                                              '& .MuiChip-icon': {
                                                color: '#0f172a',
                                              },
                                            },
                                          }}
                                          onClick={() => {
                                            setFilesAndBundlesMode(chipMode);
                                            setFilesAndBundlesCompareMode(false);
                                            setFilesAndBundlesDialogOpen(true);
                                          }}
                                        />
                                      )}
                                    </Box>
                                  );
                                })()}
                              </Box>
                              {/** Inline placeholder inputs (claimant, respondent, witness, expert, topic) in chips */}
                              {(() => {
                                const isSelected =
                                  pendingPrompt && pendingPrompt.name === item.name;
                                const needsClaimant =
                                  isSelected && !!pendingPrompt?.filterType?.claimant;
                                const needsRespondent =
                                  isSelected && !!pendingPrompt?.filterType?.respondent;
                                const needsWitness =
                                  isSelected && !!pendingPrompt?.filterType?.witness;
                                const needsExpert =
                                  isSelected && !!pendingPrompt?.filterType?.expert;
                                const needsTopic = isSelected && !!pendingPrompt?.filterType?.topic;
                                const needsParty = isSelected && !!pendingPrompt?.filterType?.party;
                                if (
                                  !needsClaimant &&
                                  !needsRespondent &&
                                  !needsWitness &&
                                  !needsExpert &&
                                  !needsTopic &&
                                  !needsParty
                                )
                                  return null;

                                const claimantLabel =
                                  pendingClaimant || T.translate('generic.claimant') || 'Claimant';
                                const respondentLabel =
                                  pendingRespondent ||
                                  T.translate('generic.respondent') ||
                                  'Respondent';
                                const witnessLabel =
                                  pendingWitness || T.translate('generic.witness') || 'Witness';
                                const expertLabel =
                                  pendingExpert || T.translate('generic.expert') || 'Expert';
                                const topicLabel =
                                  pendingTopic || T.translate('generic.topic') || 'Topic';
                                const partyLabel =
                                  pendingParty || T.translate('generic.party') || 'Party';

                                return (
                                  <Box
                                    mt={2}
                                    sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                    }}
                                  >
                                    {needsClaimant && (
                                      <Chip
                                        size="small"
                                        icon={<EditNoteOutlined />}
                                        label={claimantLabel}
                                        clickable
                                        sx={{
                                          backgroundColor: '#E0E0E0',
                                          fontSize: '0.85rem',
                                          '& .MuiChip-label': {
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          },
                                          '&:hover': {
                                            color: '#4567e5',
                                            '& .MuiChip-icon': { color: '#4567e5' },
                                          },
                                        }}
                                        onClick={(e) =>
                                          setClaimantAnchorEl(
                                            claimantAnchorEl ? null : e.currentTarget,
                                          )
                                        }
                                      />
                                    )}
                                    {needsRespondent && (
                                      <Chip
                                        size="small"
                                        icon={<EditNoteOutlined />}
                                        label={respondentLabel}
                                        clickable
                                        sx={{
                                          backgroundColor: '#E0E0E0',
                                          fontSize: '0.85rem',
                                          '& .MuiChip-label': {
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          },
                                          '&:hover': {
                                            color: '#4567e5',
                                            '& .MuiChip-icon': { color: '#4567e5' },
                                          },
                                        }}
                                        onClick={(e) =>
                                          setRespondentAnchorEl(
                                            respondentAnchorEl ? null : e.currentTarget,
                                          )
                                        }
                                      />
                                    )}
                                    {needsWitness && (
                                      <Chip
                                        size="small"
                                        icon={<EditNoteOutlined />}
                                        label={witnessLabel}
                                        clickable
                                        sx={{
                                          backgroundColor: '#E0E0E0',
                                          fontSize: '0.85rem',
                                          '& .MuiChip-label': {
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          },
                                          '&:hover': {
                                            color: '#4567e5',
                                            '& .MuiChip-icon': { color: '#4567e5' },
                                          },
                                        }}
                                        onClick={(e) =>
                                          setWitnessAnchorEl(
                                            witnessAnchorEl ? null : e.currentTarget,
                                          )
                                        }
                                      />
                                    )}
                                    {needsExpert && (
                                      <Chip
                                        size="small"
                                        icon={<EditNoteOutlined />}
                                        label={expertLabel}
                                        clickable
                                        sx={{
                                          backgroundColor: '#E0E0E0',
                                          fontSize: '0.85rem',
                                          '& .MuiChip-label': {
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          },
                                          '&:hover': {
                                            color: '#4567e5',
                                            '& .MuiChip-icon': { color: '#4567e5' },
                                          },
                                        }}
                                        onClick={(e) =>
                                          setExpertAnchorEl(expertAnchorEl ? null : e.currentTarget)
                                        }
                                      />
                                    )}
                                    {needsTopic && (
                                      <Chip
                                        size="small"
                                        icon={<EditNoteOutlined />}
                                        label={topicLabel}
                                        clickable
                                        sx={{
                                          backgroundColor: '#E0E0E0',
                                          fontSize: '0.85rem',
                                          '& .MuiChip-label': {
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          },
                                          '&:hover': {
                                            color: '#4567e5',
                                            '& .MuiChip-icon': { color: '#4567e5' },
                                          },
                                        }}
                                        onClick={(e) =>
                                          setTopicAnchorEl(topicAnchorEl ? null : e.currentTarget)
                                        }
                                      />
                                    )}
                                    {needsParty && (
                                      <Chip
                                        size="small"
                                        icon={<EditNoteOutlined />}
                                        label={partyLabel}
                                        clickable
                                        sx={{
                                          backgroundColor: '#E0E0E0',
                                          fontSize: '0.85rem',
                                          '& .MuiChip-label': {
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                          },
                                          '&:hover': {
                                            color: '#4567e5',
                                            '& .MuiChip-icon': { color: '#4567e5' },
                                          },
                                        }}
                                        onClick={(e) =>
                                          setPartyAnchorEl(partyAnchorEl ? null : e.currentTarget)
                                        }
                                      />
                                    )}
                                  </Box>
                                );
                              })()}
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: '12px',
                            }}
                          >
                            {T.translate('generic.noPromptsAvailable')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
              </>
            )}

            {mainTab === 1 && (
              <>
                {isLoading ? (
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flex: 1,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {error ? (
                      <Box sx={{ padding: 2 }}>
                        <Alert severity="error">{error}</Alert>
                      </Box>
                    ) : null}
                    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
                      {/* Custom Prompt Category Chips + Create Button */}
                      <Box
                        sx={{
                          borderBottom: '1px solid #e5e7eb',
                          backgroundColor: '#f8fafc',
                          padding: '10px 16px',
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '8px',
                        }}
                      >
                        {customPromptLibrary.map((category, index) => (
                          <Box
                            key={category.key}
                            onClick={() => {
                              handleCategoryClick(index);
                            }}
                            sx={{
                              position: 'relative',
                              padding:
                                category.categoryId && editingCategoryId === category.categoryId
                                  ? '6px 54px 6px 14px'
                                  : '6px 14px',
                              cursor: 'pointer',
                              borderRadius: '5px',
                              fontSize: '12px',
                              fontWeight: 600,
                              color: selectedCustomCategory === index ? '#000' : '#0f172a',
                              backgroundColor:
                                selectedCustomCategory === index ? '#d3dcf8' : '#f8fafc',
                              border:
                                selectedCustomCategory === index
                                  ? '1px solid #b8c5f2'
                                  : '1px solid #e5e7eb',
                              transition:
                                'background-color 160ms ease, border-color 160ms ease, color 160ms ease, transform 120ms ease, padding 160ms ease',
                              ...(category.categoryId &&
                                editingCategoryId === category.categoryId && {
                                  '& .category-actions': {
                                    opacity: 1,
                                  },
                                }),
                              '&:hover': {
                                backgroundColor:
                                  selectedCustomCategory === index ? '#d3dcf8' : '#e8ecfb',
                                borderColor:
                                  selectedCustomCategory === index ? '#b8c5f2' : '#c7d2fe',
                                color: '#000',
                                ...(category.categoryId && { paddingRight: '54px' }),
                                '& .category-actions': {
                                  opacity: 1,
                                },
                              },
                              '&:active': {
                                transform: 'scale(0.98)',
                              },
                            }}
                          >
                            {category.displayName}
                            {category.categoryId && (
                              <Box
                                className="category-actions"
                                sx={{
                                  position: 'absolute',
                                  right: '4px',
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '2px',
                                  opacity: 0,
                                  transition: 'opacity 160ms ease',
                                }}
                              >
                                <Box
                                  component="span"
                                  onClick={(e: React.MouseEvent<HTMLElement>) => {
                                    e.stopPropagation();
                                    setEditingCategoryId(category.categoryId!);
                                    setEditCategoryName(category.displayName);
                                    setEditCategoryOriginalName(category.displayName);
                                    setEditCategoryMenuAnchorEl(e.currentTarget);
                                  }}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderRadius: '3px',
                                    padding: '1px',
                                    '&:hover': {
                                      backgroundColor: 'rgba(69, 103, 229, 0.12)',
                                    },
                                  }}
                                >
                                  <EditOutlined sx={{ fontSize: '14px', color: '#4567e5' }} />
                                </Box>
                                <Box
                                  component="span"
                                  onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    setDeleteCategoryConfirm({
                                      id: category.categoryId!,
                                      name: category.displayName,
                                    });
                                  }}
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderRadius: '3px',
                                    padding: '1px',
                                    '&:hover': {
                                      backgroundColor: 'rgba(239, 68, 68, 0.12)',
                                    },
                                  }}
                                >
                                  <FontAwesomeIcon
                                    icon={faTrash}
                                    style={{ fontSize: '12px', color: '#ef4444' }}
                                  />
                                </Box>
                              </Box>
                            )}
                          </Box>
                        ))}
                        {/* + Create category button */}
                        <Tooltip title={T.translate('generic.createNewCategory')} arrow>
                          <Box
                            onClick={(e) => {
                              setCategoryMenuAnchorEl(e.currentTarget as HTMLElement);
                            }}
                            sx={{
                              padding: '6px 14px',
                              cursor: 'pointer',
                              borderRadius: '5px',
                              fontSize: '12px',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              color: '#4567e5',
                              backgroundColor: categoryMenuAnchorEl ? '#eef2ff' : 'transparent',
                              border: categoryMenuAnchorEl
                                ? '1px solid #4567e5'
                                : '1px solid #a0b4f0',
                              transition:
                                'background-color 160ms ease, border-color 160ms ease, color 160ms ease, transform 120ms ease',
                              '&:hover': {
                                backgroundColor: '#eef2ff',
                                borderColor: '#4567e5',
                              },
                              '&:active': {
                                transform: 'scale(0.98)',
                              },
                            }}
                          >
                            <AddRounded sx={{ fontSize: '16px' }} />
                          </Box>
                        </Tooltip>
                        {/* Category creation menu */}
                        <Menu
                          anchorEl={categoryMenuAnchorEl}
                          open={!!categoryMenuAnchorEl}
                          onClose={() => {
                            setCategoryMenuAnchorEl(null);
                          }}
                          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                          slotProps={{
                            paper: {
                              sx: {
                                minWidth: 280,
                                padding: '16px',
                                borderRadius: '8px',
                                boxShadow: '0 16px 44px rgba(15, 23, 42, 0.14)',
                                border: '1px solid #e5e7eb',
                              },
                            },
                          }}
                        >
                          <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            value={newCategoryName}
                            onChange={(e) => setNewCategoryName(e.target.value)}
                            placeholder={T.translate('generic.typeCategoryName') as string}
                            error={newCategoryName.length > 50}
                            helperText={
                              newCategoryName.length > 50
                                ? T.translate('generic.nameCharLimit')
                                : ''
                            }
                            onKeyDown={(e) => {
                              e.stopPropagation();
                              if (
                                e.key === 'Enter' &&
                                newCategoryName.trim() &&
                                newCategoryName.length <= 50
                              ) {
                                e.preventDefault();
                                handleCreateCategory();
                              }
                            }}
                            sx={{
                              mb: 2,
                              '& .MuiOutlinedInput-root': {
                                fontSize: '12px',
                                fontWeight: 600,
                                borderRadius: '5px',
                                backgroundColor: '#ffffff',
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#d1d5db',
                                borderWidth: '1px',
                              },
                              '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#999999',
                                borderWidth: '1px',
                              },
                              '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                                {
                                  borderColor: '#999999',
                                  borderWidth: '1px',
                                },
                            }}
                          />
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button
                              size="small"
                              onClick={() => {
                                setCategoryMenuAnchorEl(null);
                                setNewCategoryName('');
                              }}
                            >
                              {T.translate('generic.cancel')}
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              disabled={
                                !newCategoryName.trim() ||
                                newCategoryName.length > 50 ||
                                isCreatingCategory
                              }
                              onClick={handleCreateCategory}
                            >
                              {isCreatingCategory ? (
                                <CircularProgress size={16} sx={{ color: '#fff', mr: 0.5 }} />
                              ) : null}
                              {T.translate('generic.submit')}
                            </Button>
                          </Box>
                        </Menu>
                      </Box>

                      {/* + Prompt button */}
                      {mainTab === 1 && customPromptLibrary.length > 0 && (
                        <Box sx={{ padding: '8px 16px 0' }}>
                          <Box
                            onClick={(e: React.MouseEvent<HTMLElement>) =>
                              setPromptMenuAnchorEl(e.currentTarget)
                            }
                            sx={{
                              padding: '6px 14px',
                              cursor: 'pointer',
                              borderRadius: '5px',
                              fontSize: '12px',
                              fontWeight: 600,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                              color: '#4567e5',
                              border: promptMenuAnchorEl
                                ? '1px solid #4567e5'
                                : '1px solid #a0b4f0',
                              backgroundColor: promptMenuAnchorEl ? '#eef2ff' : 'transparent',
                              transition:
                                'background-color 160ms ease, border-color 160ms ease, color 160ms ease, transform 120ms ease',
                              '&:hover': {
                                backgroundColor: '#eef2ff',
                                borderColor: '#4567e5',
                              },
                              '&:active': {
                                transform: 'scale(0.98)',
                              },
                            }}
                          >
                            <AddRounded sx={{ fontSize: '16px' }} />
                            {T.translate('generic.addPrompt')}
                          </Box>
                          {/* Prompt creation menu */}
                          <Menu
                            anchorEl={promptMenuAnchorEl}
                            open={!!promptMenuAnchorEl}
                            onClose={() => {
                              setPromptMenuAnchorEl(null);
                            }}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                            slotProps={{
                              paper: {
                                sx: {
                                  minWidth: 1024,
                                  padding: '16px',
                                  borderRadius: '8px',
                                  boxShadow: '0 16px 44px rgba(15, 23, 42, 0.14)',
                                  border: '1px solid #e5e7eb',
                                },
                              },
                            }}
                          >
                            <TextField
                              autoFocus
                              fullWidth
                              size="small"
                              label={T.translate('generic.name') as string}
                              value={newPromptName}
                              onChange={(e) => setNewPromptName(e.target.value)}
                              placeholder={T.translate('generic.typePromptName') as string}
                              error={newPromptName.length > 50}
                              InputLabelProps={{ shrink: true }}
                              helperText={
                                newPromptName.length > 50
                                  ? T.translate('generic.nameCharLimit')
                                  : ''
                              }
                              onKeyDown={(e) => {
                                e.stopPropagation();
                                if (
                                  e.key === 'Enter' &&
                                  newPromptName.trim() &&
                                  newPromptName.length <= 50 &&
                                  newPromptText.trim() &&
                                  newPromptText.length <= 10000 &&
                                  !isImprovingNewPrompt
                                ) {
                                  e.preventDefault();
                                  handleCreatePrompt();
                                }
                              }}
                              sx={{
                                mb: 2,
                                '& .MuiOutlinedInput-root': {
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  borderRadius: '5px',
                                  backgroundColor: '#ffffff',
                                },
                                '& .MuiInputLabel-root': {
                                  fontSize: '12px',
                                  fontWeight: 600,
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#d1d5db',
                                  borderWidth: '1px',
                                },
                                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#999999',
                                  borderWidth: '1px',
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                                  {
                                    borderColor: '#999999',
                                    borderWidth: '1px',
                                  },
                              }}
                            />
                            <TextField
                              fullWidth
                              size="small"
                              multiline
                              label={T.translate('generic.prompt') as string}
                              value={newPromptText}
                              onChange={(e) => setNewPromptText(e.target.value)}
                              placeholder={T.translate('generic.typeThePrompt') as string}
                              error={newPromptText.length > 10000}
                              InputLabelProps={{ shrink: true }}
                              helperText={
                                newPromptText.length > 10000
                                  ? T.translate('generic.promptCharLimit')
                                  : ''
                              }
                              onKeyDown={(e) => {
                                e.stopPropagation();
                                if (
                                  e.key === 'Enter' &&
                                  !e.shiftKey &&
                                  newPromptName.trim() &&
                                  newPromptName.length <= 50 &&
                                  newPromptText.trim() &&
                                  newPromptText.length <= 10000 &&
                                  !isCreatingPrompt &&
                                  !isImprovingNewPrompt
                                ) {
                                  e.preventDefault();
                                  handleCreatePrompt();
                                }
                              }}
                              sx={{
                                mb: 1,
                                '& .MuiOutlinedInput-root': {
                                  fontSize: '12px',
                                  borderRadius: '5px',
                                  backgroundColor: '#ffffff',
                                  alignItems: 'flex-start',
                                },
                                '& .MuiInputBase-inputMultiline': {
                                  height: 'calc(85vh * 0.5 - 100px) !important',
                                  overflow: 'auto !important',
                                },
                                '& .MuiInputLabel-root': {
                                  fontSize: '12px',
                                  fontWeight: 600,
                                },
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#d1d5db',
                                  borderWidth: '1px',
                                },
                                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#999999',
                                  borderWidth: '1px',
                                },
                                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                                  {
                                    borderColor: '#999999',
                                    borderWidth: '1px',
                                  },
                              }}
                            />
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                minHeight: '28px',
                                gap: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  minHeight: '28px',
                                  gap: 1,
                                }}
                              >
                                {newPromptText.trim() ? (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="primary"
                                    onClick={handleImproveNewPrompt}
                                    disabled={isImprovingNewPrompt}
                                    startIcon={
                                      isImprovingNewPrompt ? undefined : (
                                        <AutoAwesome fontSize="small" />
                                      )
                                    }
                                    sx={improvePromptButtonSx}
                                  >
                                    {isImprovingNewPrompt ? (
                                      <CircularProgress
                                        size={16}
                                        sx={{ color: '#4567e5', mr: 0.5 }}
                                      />
                                    ) : null}
                                    {improvePromptLabel}
                                  </Button>
                                ) : null}
                                {newPromptOriginalText ? (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="inherit"
                                    onClick={handleResetNewPrompt}
                                    startIcon={<Undo fontSize="small" />}
                                    sx={resetPromptButtonSx}
                                  >
                                    {resetPromptButtonLabel}
                                  </Button>
                                ) : null}
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Button
                                  size="small"
                                  onClick={() => {
                                    newPromptImproveRequestIdRef.current += 1;
                                    setIsImprovingNewPrompt(false);
                                    setPromptMenuAnchorEl(null);
                                    setNewPromptName('');
                                    setNewPromptText('');
                                    setNewPromptOriginalText('');
                                  }}
                                >
                                  {T.translate('generic.cancel')}
                                </Button>
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={
                                    !newPromptName.trim() ||
                                    newPromptName.length > 50 ||
                                    !newPromptText.trim() ||
                                    newPromptText.length > 10000 ||
                                    isCreatingPrompt ||
                                    isImprovingNewPrompt
                                  }
                                  onClick={handleCreatePrompt}
                                >
                                  {isCreatingPrompt ? (
                                    <CircularProgress size={16} sx={{ color: '#fff', mr: 0.5 }} />
                                  ) : null}
                                  {T.translate('generic.submit')}
                                </Button>
                              </Box>
                            </Box>
                          </Menu>
                        </Box>
                      )}

                      {/* Custom Prompt Items List */}
                      <Box
                        sx={{
                          flex: 1,
                          minHeight: 0,
                          overflowY: 'auto',
                          padding: 2,
                          backgroundColor: '#f8fafc',
                          '&::-webkit-scrollbar': {
                            width: '4px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(0,0,0,0.18)',
                            borderRadius: '4px',
                          },
                          '&::-webkit-scrollbar-thumb:hover': {
                            background: 'rgba(0,0,0,0.3)',
                          },
                          scrollbarWidth: 'thin',
                          scrollbarColor: 'rgba(0,0,0,0.18) transparent',
                        }}
                      >
                        {currentSubCategories.length > 0 ? (
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
                              gap: 2,
                            }}
                          >
                            {currentSubCategories.map((item, index) => {
                              const isEditing = editingPromptId === item.id;
                              const hasChanges =
                                isEditing &&
                                (editName !== item.name || editPromptText !== item.prompt);
                              const canUpdate =
                                hasChanges &&
                                editName.trim() &&
                                editName.length <= 50 &&
                                editPromptText.trim() &&
                                editPromptText.length <= 10000 &&
                                !isUpdating &&
                                !isImprovingEditPrompt;

                              return (
                                <Box
                                  key={index}
                                  onClick={() => {
                                    if (!isEditing) handleSubCategoryClick(item);
                                  }}
                                  sx={{
                                    padding: 2,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    border:
                                      isEditing || selectedSubCategory === item.name
                                        ? '1px solid #c7d2fe'
                                        : '1px solid #e5e7eb',
                                    borderRadius: '12px',
                                    cursor: isEditing ? 'default' : 'pointer',
                                    transition:
                                      'background-color 160ms ease, border-color 160ms ease, transform 120ms ease, box-shadow 160ms ease',
                                    backgroundColor:
                                      isEditing || selectedSubCategory === item.name
                                        ? '#eef2ff'
                                        : '#ffffff',
                                    ...(!isEditing && {
                                      '&:hover': {
                                        backgroundColor:
                                          selectedSubCategory === item.name ? '#eef2ff' : '#f1f5f9',
                                        borderColor:
                                          selectedSubCategory === item.name ? '#c7d2fe' : '#cbd5e1',
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 4px 12px rgba(69, 103, 229, 0.08)',
                                        '& .prompt-actions': {
                                          opacity: 1,
                                        },
                                      },
                                      '&:active': {
                                        transform: 'translateY(0)',
                                      },
                                    }),
                                  }}
                                >
                                  {!isEditing && selectedSubCategory === item.name && (
                                    <CheckCircle
                                      fontSize="small"
                                      sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        color: '#4567e5',
                                      }}
                                    />
                                  )}

                                  {/* Edit overlay — floats independently */}
                                  {isEditing && (
                                    <Box
                                      ref={editBoxRef}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) {
                                          e.preventDefault();
                                          if (canUpdate) {
                                            handleUpdateCustomPrompt();
                                          }
                                        }
                                      }}
                                      sx={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        zIndex: 10,
                                        width: 1024,
                                        padding: 2,
                                        backgroundColor: '#ffffff',
                                        border: '1px solid #e5e7eb',
                                        borderRadius: '12px',
                                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
                                      }}
                                    >
                                      {/* Editable Name */}
                                      <TextField
                                        fullWidth
                                        size="small"
                                        label={T.translate('generic.name') as string}
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        variant="outlined"
                                        onClick={(e) => e.stopPropagation()}
                                        error={editName.length > 50}
                                        InputLabelProps={{ shrink: true }}
                                        helperText={
                                          editName.length > 50
                                            ? T.translate('generic.nameCharLimit')
                                            : ''
                                        }
                                        sx={{
                                          mb: 1.5,
                                          '& .MuiOutlinedInput-root': {
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            borderRadius: '5px',
                                            backgroundColor: '#ffffff',
                                          },
                                          '& .MuiInputLabel-root': {
                                            fontSize: '12px',
                                            fontWeight: 600,
                                          },
                                          '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#d1d5db',
                                            borderWidth: '1px',
                                          },
                                          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                                            {
                                              borderColor: '#999999',
                                              borderWidth: '1px',
                                            },
                                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                                            {
                                              borderColor: '#999999',
                                              borderWidth: '1px',
                                            },
                                        }}
                                      />
                                      {/* Editable Prompt */}
                                      <TextField
                                        fullWidth
                                        multiline
                                        label={T.translate('generic.prompt') as string}
                                        value={editPromptText}
                                        onChange={(e) => setEditPromptText(e.target.value)}
                                        variant="outlined"
                                        onClick={(e) => e.stopPropagation()}
                                        error={editPromptText.length > 10000}
                                        InputLabelProps={{ shrink: true }}
                                        helperText={
                                          editPromptText.length > 10000
                                            ? T.translate('generic.promptCharLimit')
                                            : ''
                                        }
                                        sx={{
                                          mb: 0.75,
                                          '& .MuiOutlinedInput-root': {
                                            fontSize: '12px',
                                            borderRadius: '5px',
                                            backgroundColor: '#ffffff',
                                            alignItems: 'flex-start',
                                          },
                                          '& .MuiInputBase-inputMultiline': {
                                            height: 'calc(85vh * 0.5 - 100px) !important',
                                            overflow: 'auto !important',
                                          },
                                          '& .MuiInputLabel-root': {
                                            fontSize: '12px',
                                            fontWeight: 600,
                                          },
                                          '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: '#d1d5db',
                                            borderWidth: '1px',
                                          },
                                          '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline':
                                            {
                                              borderColor: '#999999',
                                              borderWidth: '1px',
                                            },
                                          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline':
                                            {
                                              borderColor: '#999999',
                                              borderWidth: '1px',
                                            },
                                        }}
                                      />
                                      {/* Cancel + Update buttons */}
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          alignItems: 'center',
                                          minHeight: '28px',
                                          gap: 1,
                                        }}
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            minHeight: '28px',
                                            gap: 1,
                                          }}
                                        >
                                          {editPromptText.trim() ? (
                                            <Button
                                              size="small"
                                              variant="contained"
                                              color="primary"
                                              onClick={handleImproveEditPrompt}
                                              disabled={isImprovingEditPrompt}
                                              startIcon={
                                                isImprovingEditPrompt ? undefined : (
                                                  <AutoAwesome fontSize="small" />
                                                )
                                              }
                                              sx={improvePromptButtonSx}
                                            >
                                              {isImprovingEditPrompt ? (
                                                <CircularProgress
                                                  size={16}
                                                  sx={{ color: '#4567e5', mr: 0.5 }}
                                                />
                                              ) : null}
                                              {improvePromptLabel}
                                            </Button>
                                          ) : null}
                                          {editPromptOriginalText ? (
                                            <Button
                                              size="small"
                                              variant="outlined"
                                              color="inherit"
                                              onClick={handleResetEditPrompt}
                                              startIcon={<Undo fontSize="small" />}
                                              sx={resetPromptButtonSx}
                                            >
                                              {resetPromptButtonLabel}
                                            </Button>
                                          ) : null}
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Button size="small" onClick={handleCancelEdit}>
                                            {T.translate('generic.cancel')}
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            disabled={!canUpdate}
                                            onClick={() => handleUpdateCustomPrompt()}
                                          >
                                            {isUpdating ? (
                                              <CircularProgress
                                                size={16}
                                                sx={{ color: '#fff', mr: 0.5 }}
                                              />
                                            ) : null}
                                            {T.translate('generic.update')}
                                          </Button>
                                        </Box>
                                      </Box>
                                    </Box>
                                  )}

                                  {/* Normal card content — always rendered to maintain card size */}
                                  {!isEditing && (
                                    <>
                                      <Box sx={{ paddingTop: '24px' }}>
                                        <Typography
                                          variant="subtitle2"
                                          sx={{
                                            fontSize: '12px',
                                            fontWeight: 600,
                                            marginBottom: 1,
                                            color:
                                              selectedSubCategory === item.name
                                                ? '#4567e5'
                                                : '#0f172a',
                                          }}
                                        >
                                          {item.name}
                                        </Typography>
                                        <Typography
                                          variant="body1"
                                          sx={{
                                            color: '#64748b',
                                            fontSize: '12px',
                                            lineHeight: 1.5,
                                          }}
                                        >
                                          {renderPromptText(
                                            item.prompt,
                                            selectedSubCategory === item.name,
                                          )}
                                        </Typography>
                                      </Box>
                                      {/* Move + Edit + Delete icon buttons (top-right, shown on hover) */}
                                      <Box
                                        className="prompt-actions"
                                        sx={{
                                          position: 'absolute',
                                          right: selectedSubCategory === item.name ? '32px' : '8px',
                                          top: '8px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '4px',
                                          opacity: 0,
                                          transition: 'opacity 160ms ease',
                                        }}
                                      >
                                        <Tooltip title={movePromptTooltip} arrow>
                                          <IconButton
                                            size="small"
                                            onClick={(e) =>
                                              handleOpenMovePromptMenu(e, item, activeCategory)
                                            }
                                            aria-label={`Move ${item.name}`}
                                            sx={{
                                              padding: '2px',
                                              borderRadius: '4px',
                                              '&:hover': {
                                                backgroundColor: 'rgba(71, 85, 105, 0.12)',
                                              },
                                            }}
                                          >
                                            <MoveUp sx={{ fontSize: '14px', color: '#475569' }} />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title={editPromptTooltip} arrow>
                                          <IconButton
                                            size="small"
                                            onClick={(e: React.MouseEvent<HTMLElement>) => {
                                              e.stopPropagation();
                                              handleStartEdit(item);
                                            }}
                                            aria-label={`${T.translate('generic.edit')} ${item.name}`}
                                            sx={{
                                              padding: '2px',
                                              borderRadius: '4px',
                                              '&:hover': {
                                                backgroundColor: 'rgba(69, 103, 229, 0.12)',
                                              },
                                            }}
                                          >
                                            <EditOutlined
                                              sx={{ fontSize: '14px', color: '#4567e5' }}
                                            />
                                          </IconButton>
                                        </Tooltip>
                                        <Tooltip title={deletePromptTooltip} arrow>
                                          <IconButton
                                            size="small"
                                            onClick={(e: React.MouseEvent) => {
                                              e.stopPropagation();
                                              if (item.id)
                                                setDeleteConfirm({ id: item.id, name: item.name });
                                            }}
                                            aria-label={`${T.translate('generic.delete')} ${item.name}`}
                                            sx={{
                                              padding: '2px',
                                              borderRadius: '4px',
                                              '&:hover': {
                                                backgroundColor: 'rgba(239, 68, 68, 0.12)',
                                              },
                                            }}
                                          >
                                            <FontAwesomeIcon
                                              icon={faTrash}
                                              style={{ fontSize: '12px', color: '#ef4444' }}
                                            />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </>
                                  )}
                                </Box>
                              );
                            })}
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              height: '100%',
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontSize: '12px',
                              }}
                            >
                              {T.translate('generic.noPromptsAvailable')}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </>
                )}
              </>
            )}

            {showStandingInstructionsTab && mainTab === 2 && (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                  padding: 2,
                  backgroundColor: '#f8fafc',
                  '&::-webkit-scrollbar': {
                    width: '4px',
                  },
                  '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    background: 'rgba(0,0,0,0.18)',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb:hover': {
                    background: 'rgba(0,0,0,0.3)',
                  },
                  scrollbarWidth: 'thin',
                  scrollbarColor: 'rgba(0,0,0,0.18) transparent',
                }}
              >
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))',
                    gap: 2,
                  }}
                >
                  {(['context', 'outputRules'] as StandingInstructionKey[]).map((type) => {
                    const config = standingInstructionConfig[type];
                    const value = getStandingInstructionValue(type);

                    if (!value) {
                      return (
                        <Box
                          key={type}
                          role="button"
                          tabIndex={0}
                          onClick={() => handleOpenStandingInstructionMenu(type)}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              handleOpenStandingInstructionMenu(type);
                            }
                          }}
                          sx={{
                            minHeight: 180,
                            padding: 2,
                            border: '1px dashed #cbd5e1',
                            borderRadius: '12px',
                            backgroundColor: '#ffffff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            cursor: 'pointer',
                            position: 'relative',
                          }}
                        >
                          {editingStandingInstruction === type &&
                            renderStandingInstructionEditor(type)}
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontSize: '13px',
                              fontWeight: 700,
                              color: '#4567e5',
                            }}
                          >
                            {config.addLabel}
                          </Typography>
                        </Box>
                      );
                    }

                    return (
                      <Box
                        key={type}
                        sx={{
                          minHeight: 180,
                          padding: 2,
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px',
                          backgroundColor: '#ffffff',
                          position: 'relative',
                          '&:hover .standing-instruction-edit-action': {
                            opacity: 1,
                          },
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontSize: '12px',
                            fontWeight: 700,
                            marginBottom: 1,
                            color: '#0f172a',
                          }}
                        >
                          {config.label}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: '#64748b',
                            fontSize: '12px',
                            lineHeight: 1.5,
                            pr: 3,
                          }}
                        >
                          {renderLinesWithBreaks(value)}
                        </Typography>
                        <Box
                          component="button"
                          className="standing-instruction-edit-action"
                          type="button"
                          aria-label={`${T.translate('generic.edit')} ${config.label}`}
                          onClick={() => handleOpenStandingInstructionMenu(type)}
                          sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 24,
                            height: 24,
                            borderRadius: '6px',
                            border: 'none',
                            backgroundColor: 'transparent',
                            color: '#4567e5',
                            cursor: 'pointer',
                            opacity: 0,
                            transition: 'opacity 160ms ease',
                          }}
                        >
                          <EditOutlined sx={{ fontSize: '16px' }} />
                        </Box>
                        {editingStandingInstruction === type &&
                          renderStandingInstructionEditor(type)}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
          </>
        </DialogContent>

        <Divider />

        <DialogActions sx={{ padding: 2 }}>
          {/** Disabled reason moved to the far left, before Close button */}
          {!isStandingInstructionsView &&
            (() => {
              const externalFiles = Array.isArray(filters?.files) ? filters.files : [];
              const hasPrompt = !!pendingPrompt || !!filters?.promptName;
              const isCompareCategory = currentCategoryDisplayName === 'compare';
              const selectedPrompt = pendingPrompt;
              const promptRequiresFiles = !!(
                selectedPrompt?.filterType?.documents ||
                selectedPrompt?.filterType?.multipleDocuments
              );
              const promptRequiresBundles = !!(
                selectedPrompt?.filterType?.bundles || selectedPrompt?.filterType?.multipleBundles
              );
              const isComparisonPrompt = !!selectedPrompt?.filterType?.isComparison;
              const isApplyRendered = pendingFiles.length > 0 || isCompareCategory || pendingPrompt;
              // Compare prompts - allow 2 or more files
              const compareDisabled = (() => {
                if (!(isCompareCategory || isComparisonPrompt)) return false;

                const hasEnoughExternalFiles = externalFiles.length >= 2;
                const hasEnoughPendingFiles = pendingFiles.length >= 2;
                return !(
                  (hasEnoughExternalFiles &&
                    String(filters?.promptCategory || '').toLowerCase() === 'compare' &&
                    !!filters?.promptName) ||
                  (hasEnoughExternalFiles && !!pendingPrompt) ||
                  (hasEnoughPendingFiles && !!pendingPrompt)
                );
              })();

              // Non-compare prompts that require at least 1 file
              const genericFilePromptDisabled =
                !isComparisonPrompt && promptRequiresFiles && pendingFiles.length === 0;
              const genericBundlePromptDisabled =
                !isComparisonPrompt && promptRequiresBundles && pendingBundles.length === 0;
              const promptRequiresAnyFilesOrBundles =
                !!selectedPrompt?.filterType?.anyFilesOrBundles;
              const genericAnyFilesOrBundlesDisabled =
                promptRequiresAnyFilesOrBundles &&
                pendingFiles.length === 0 &&
                pendingBundles.length === 0;
              // (compareDisabled || genericFilePromptDisabled) combined result not directly needed beyond messages
              let disabledReason: string | null = null;
              if ((isCompareCategory || isComparisonPrompt) && isApplyRendered && compareDisabled) {
                const hasEnoughFiles = externalFiles.length >= 2 || pendingFiles.length >= 2;
                if (!hasEnoughFiles && !hasPrompt) {
                  disabledReason =
                    T.translate('generic.selectPromptAndAtLeastTwoFilesToEnableApply') ||
                    'Select a Compare prompt and at least two files to enable Apply.';
                } else if (!hasEnoughFiles) {
                  disabledReason =
                    T.translate('generic.selectAtLeastTwoFilesToEnableApply') ||
                    'Select at least two files to compare.';
                } else if (!hasPrompt) {
                  disabledReason =
                    T.translate('generic.selectComparePromptToEnableApply') ||
                    'Select a Compare prompt to enable Apply.';
                }
              } else if (genericAnyFilesOrBundlesDisabled && isApplyRendered) {
                disabledReason =
                  T.translate('generic.selectFilesOrBundlesToEnableApply') ||
                  'Select at least one file or one bundle to enable Apply.';
              } else if (
                genericFilePromptDisabled &&
                !promptRequiresAnyFilesOrBundles &&
                isApplyRendered
              ) {
                disabledReason =
                  T.translate('generic.selectAtLeastOneFileToEnableApply') ||
                  'Select at least one file to enable Apply.';
              } else if (genericBundlePromptDisabled && isApplyRendered) {
                disabledReason =
                  T.translate('generic.selectAtLeastOneBundleToEnableApply') ||
                  'Select at least one bundle to enable Apply.';
              } else if (isApplyRendered && selectedPrompt) {
                // Check for placeholder requirements
                const requiresClaimant = !!selectedPrompt.filterType?.claimant;
                const requiresRespondent = !!selectedPrompt.filterType?.respondent;
                const requiresWitness = !!selectedPrompt.filterType?.witness;
                const requiresExpert = !!selectedPrompt.filterType?.expert;
                const requiresTopic = !!selectedPrompt.filterType?.topic;
                const requiresParty = !!selectedPrompt.filterType?.party;

                const claimantMissing = requiresClaimant && !pendingClaimant.trim();
                const respondentMissing = requiresRespondent && !pendingRespondent.trim();
                const witnessMissing = requiresWitness && !pendingWitness.trim();
                const expertMissing = requiresExpert && !pendingExpert.trim();
                const topicMissing = requiresTopic && !pendingTopic.trim();
                const partyMissing = requiresParty && !pendingParty.trim();

                if (
                  claimantMissing &&
                  respondentMissing &&
                  witnessMissing &&
                  expertMissing &&
                  topicMissing &&
                  partyMissing
                ) {
                  disabledReason =
                    T.translate('generic.enterPromptPlaceholderInfo') ||
                    'Enter all required placeholder values';
                } else if (claimantMissing) {
                  disabledReason = T.translate('generic.enterClaimant') || 'Enter Claimant';
                } else if (respondentMissing) {
                  disabledReason = T.translate('generic.enterRespondent') || 'Enter Respondent';
                } else if (witnessMissing) {
                  disabledReason = T.translate('generic.enterWitness') || 'Enter Witness';
                } else if (expertMissing) {
                  disabledReason = T.translate('generic.enterExpert') || 'Enter Expert';
                } else if (topicMissing) {
                  disabledReason = T.translate('generic.enterTopic') || 'Enter Topic';
                } else if (partyMissing) {
                  disabledReason = T.translate('generic.enterParty') || 'Enter Party';
                }
              }
              return disabledReason ? (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#4567e5',
                    alignSelf: 'center',
                    mr: '50px', // required gap to Close button
                    fontSize: '14px', // increased font size
                    fontWeight: 500,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {disabledReason}
                </Typography>
              ) : null;
            })()}
          <Button
            onClick={handleClose}
            variant="contained"
            color="inherit"
            sx={{
              color: '#fff',
              bgcolor: 'text.disabled',
              ':hover': { bgcolor: 'text.secondary' },
            }}
          >
            {T.translate('generic.close')}
          </Button>
          {/** Only show Save when compare category is active or pending files exist */}
          {!isStandingInstructionsView &&
            (pendingFiles.length > 0 ||
              currentCategoryDisplayName === 'compare' ||
              pendingPrompt) && (
              <Button
                onClick={() => {
                  // Robust filter determination based on prompt.filterType flags
                  const ft = (pendingPrompt?.filterType || {}) as any;
                  const allowDocuments = !!ft.documents;
                  const isComparison = !!ft.isComparison;
                  const allowBundles = !!ft.bundles;
                  const allowMultiple = !!ft.multipleDocuments;
                  const isSticky = !!ft.sticky;
                  const isAnyFilesOrBundles = !!ft.anyFilesOrBundles;

                  // For anyFilesOrBundles, treat the user's actual selection as the relevant flag
                  const effectiveAllowDocuments =
                    allowDocuments ||
                    allowMultiple ||
                    isSticky ||
                    (isAnyFilesOrBundles && pendingFiles.length > 0);
                  const effectiveAllowBundles =
                    allowBundles || (isAnyFilesOrBundles && pendingBundles.length > 0);

                  // Determine resultant files and bundles based on what the prompt requires
                  // and what the user has selected. ALWAYS preserve existing filter values
                  // unless the prompt explicitly requires them and user made selections.
                  let resultantFiles: any[];
                  let resultantBundles: any[];
                  let resultantClaimant: string;
                  let resultantRespondent: string;
                  let resultantParty: string;

                  if (effectiveAllowDocuments) {
                    // Prompt requires files - use pending selections (which were initialized from filters)
                    resultantFiles = pendingFiles;
                  } else {
                    // Prompt doesn't require files - preserve whatever was in filters
                    resultantFiles = Array.isArray(filters?.files) ? filters.files : [];
                  }

                  if (effectiveAllowBundles) {
                    // Prompt requires bundles - use pending selections (which were initialized from filters)
                    resultantBundles = pendingBundles;
                  } else {
                    // Prompt doesn't require bundles - preserve whatever was in filters
                    resultantBundles = Array.isArray(filters?.location) ? filters.location : [];
                  }

                  // Handle claimant, respondent, and party
                  const allowClaimant = !!ft.claimant;
                  const allowRespondent = !!ft.respondent;
                  const allowParty = !!ft.party;

                  if (allowClaimant) {
                    resultantClaimant = pendingClaimant;
                  } else {
                    resultantClaimant = filters?.claimant || '';
                  }

                  if (allowRespondent) {
                    resultantRespondent = pendingRespondent;
                  } else {
                    resultantRespondent = filters?.respondent || '';
                  }

                  if (allowParty) {
                    resultantParty = pendingParty;
                  } else {
                    resultantParty = filters?.party || '';
                  }

                  const combined = {
                    prompt: promptWithReplacements,
                    files: resultantFiles,
                    location: resultantBundles,
                    compare: isComparison,
                    claimant: resultantClaimant,
                    respondent: resultantRespondent,
                    party: resultantParty,
                  };

                  if (onSaveSelections) {
                    // Preferred new unified callback
                    onSaveSelections(combined);
                  } else {
                    // Fallback to legacy separate callbacks if unified one not provided
                    if (
                      (pendingFiles.length > 0 || currentCategoryDisplayName === 'compare') &&
                      onFilesChange
                    ) {
                      onFilesChange(pendingFiles, pendingCompareMode);
                    }
                    if (promptWithReplacements && onSelect) {
                      onSelect(promptWithReplacements);
                    }
                  }
                  handleClose(); // Close dialog after save
                }}
                variant="contained"
                disabled={(() => {
                  const selectedPrompt = pendingPrompt;
                  // Keep button disabled if no prompt is selected
                  if (!selectedPrompt) return true;

                  const requiresFiles = !!(
                    selectedPrompt.filterType?.documents ||
                    selectedPrompt.filterType?.multipleDocuments
                  );
                  const requiresBundles = !!(
                    selectedPrompt.filterType?.bundles || selectedPrompt.filterType?.multipleBundles
                  );
                  const requiresClaimant = !!selectedPrompt.filterType?.claimant;
                  const requiresRespondent = !!selectedPrompt.filterType?.respondent;
                  const requiresWitness = !!selectedPrompt.filterType?.witness;
                  const requiresExpert = !!selectedPrompt.filterType?.expert;
                  const requiresTopic = !!selectedPrompt.filterType?.topic;
                  const requiresParty = !!selectedPrompt.filterType?.party;
                  const isComparisonPrompt = !!selectedPrompt.filterType?.isComparison;

                  // Check if claimant is required but not filled
                  if (requiresClaimant && !pendingClaimant.trim()) return true;
                  // Check if respondent is required but not filled
                  if (requiresRespondent && !pendingRespondent.trim()) return true;
                  if (requiresWitness && !pendingWitness.trim()) return true;
                  if (requiresExpert && !pendingExpert.trim()) return true;
                  if (requiresTopic && !pendingTopic.trim()) return true;
                  if (requiresParty && !pendingParty.trim()) return true;

                  if (isComparisonPrompt) {
                    // Allow 2 or more files for comparison
                    return !(
                      (Array.isArray(filters?.files) &&
                        filters.files.length >= 2 &&
                        String(filters?.promptCategory || '').toLowerCase() === 'compare' &&
                        !!filters?.promptName) ||
                      (Array.isArray(filters?.files) &&
                        filters.files.length >= 2 &&
                        !!pendingPrompt) ||
                      (pendingFiles.length >= 2 && !!pendingPrompt)
                    );
                  }
                  if (requiresFiles && pendingFiles.length === 0) return true;
                  if (requiresBundles && pendingBundles.length === 0) return true;
                  const requiresAnyFilesOrBundles = !!selectedPrompt.filterType?.anyFilesOrBundles;
                  if (
                    requiresAnyFilesOrBundles &&
                    pendingFiles.length === 0 &&
                    pendingBundles.length === 0
                  )
                    return true;
                  return false;
                })()}
                color="primary"
                sx={{ ml: 1 }}
              >
                {T.translate('generic.apply')}
              </Button>
            )}
        </DialogActions>
        {/* Files and Bundles selection dialog */}
        <FilesAndBundlesSelectionDialog
          open={filesAndBundlesDialogOpen}
          onClose={() => setFilesAndBundlesDialogOpen(false)}
          onSave={({ files, bundles }) => {
            if (filesAndBundlesCompareMode) {
              handleFilesChange(files, true);
              setPendingCompareMode(files.length > 0);
              setPendingBundles([]);
            } else if (files.length > 0) {
              handleFilesChange(files, false);
              setPendingBundles([]);
            } else if (bundles.length > 0) {
              setPendingBundles(bundles);
              setPendingFiles([]);
              setPendingCompareMode(false);
            } else {
              if (filesAndBundlesMode === 'files' || filesAndBundlesMode === 'both') {
                handleFilesChange([], false);
              }
              if (filesAndBundlesMode === 'bundles' || filesAndBundlesMode === 'both') {
                setPendingBundles([]);
              }
            }
            setFilesAndBundlesDialogOpen(false);
          }}
          initialSelectedFiles={pendingFiles}
          initialSelectedBundles={pendingBundles}
          allowedMode={filesAndBundlesMode}
          compareMode={filesAndBundlesCompareMode}
        />
        {/* Claimant input dialog */}
        {claimantAnchorEl && (
          <PromptPlaceholderInput
            value={pendingClaimant}
            onChange={(newVal: string) => setPendingClaimant(newVal)}
            anchorEl={claimantAnchorEl}
            isOpen={Boolean(claimantAnchorEl)}
            onClose={() => setClaimantAnchorEl(null)}
            type="claimant"
          />
        )}
        {/* Respondent input dialog */}
        {respondentAnchorEl && (
          <PromptPlaceholderInput
            value={pendingRespondent}
            onChange={(newVal: string) => setPendingRespondent(newVal)}
            anchorEl={respondentAnchorEl}
            isOpen={Boolean(respondentAnchorEl)}
            onClose={() => setRespondentAnchorEl(null)}
            type="respondent"
          />
        )}
        {/* Witness input dialog */}
        {witnessAnchorEl && (
          <PromptPlaceholderInput
            value={pendingWitness}
            onChange={(newVal: string) => setPendingWitness(newVal)}
            anchorEl={witnessAnchorEl}
            isOpen={Boolean(witnessAnchorEl)}
            onClose={() => setWitnessAnchorEl(null)}
            type="witness"
          />
        )}
        {/* Expert input dialog */}
        {expertAnchorEl && (
          <PromptPlaceholderInput
            value={pendingExpert}
            onChange={(newVal: string) => setPendingExpert(newVal)}
            anchorEl={expertAnchorEl}
            isOpen={Boolean(expertAnchorEl)}
            onClose={() => setExpertAnchorEl(null)}
            type="expert"
          />
        )}
        {/* Topic input dialog */}
        {topicAnchorEl && (
          <PromptPlaceholderInput
            value={pendingTopic}
            onChange={(newVal: string) => setPendingTopic(newVal)}
            anchorEl={topicAnchorEl}
            isOpen={Boolean(topicAnchorEl)}
            onClose={() => setTopicAnchorEl(null)}
            type="topic"
          />
        )}
        {/* Party input dialog */}
        {partyAnchorEl && (
          <PromptPlaceholderInput
            value={pendingParty}
            onChange={(newVal: string) => setPendingParty(newVal)}
            anchorEl={partyAnchorEl}
            isOpen={Boolean(partyAnchorEl)}
            onClose={() => setPartyAnchorEl(null)}
            type="party"
          />
        )}
      </Dialog>

      <Menu
        anchorEl={movePromptAnchorEl}
        open={!!movePromptAnchorEl}
        onClose={handleCloseMovePromptMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 320,
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 16px 44px rgba(15, 23, 42, 0.14)',
              border: '1px solid #e5e7eb',
            },
          },
        }}
      >
        <TextField
          select
          autoFocus
          fullWidth
          size="small"
          label={selectPromptMoveCategoryLabel as string}
          value={movePromptCategoryId}
          onChange={handleMovePromptCategoryChange}
          disabled={isMovingPrompt}
          InputLabelProps={{ shrink: true }}
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: '12px',
              fontWeight: 600,
              borderRadius: '5px',
              backgroundColor: '#ffffff',
            },
            '& .MuiInputLabel-root': {
              fontSize: '12px',
              fontWeight: 600,
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#d1d5db',
              borderWidth: '1px',
            },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#999999',
              borderWidth: '1px',
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#999999',
              borderWidth: '1px',
            },
          }}
        >
          {customPromptLibrary.map((category) => {
            const categoryId = String(category.categoryId || '').trim();

            return (
              <MenuItem
                key={categoryId || category.key}
                value={categoryId || `missing-${category.key}`}
                disabled={!categoryId}
              >
                {category.displayName}
              </MenuItem>
            );
          })}
        </TextField>
        {isMovingPrompt ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : null}
      </Menu>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 700 }}>
          {T.translate('generic.deletePromptTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '13px' }}>
            {T.translate('generic.confirmDeletePrompt', { name: deleteConfirm?.name || '' })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button size="small" onClick={() => setDeleteConfirm(null)}>
            {T.translate('generic.cancel')}
          </Button>
          <Button size="small" variant="contained" onClick={handleConfirmDelete}>
            {T.translate('generic.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category edit menu */}
      <Menu
        anchorEl={editCategoryMenuAnchorEl}
        open={!!editCategoryMenuAnchorEl}
        onClose={() => {
          setEditCategoryMenuAnchorEl(null);
          setEditingCategoryId(null);
          setEditCategoryName('');
          setEditCategoryOriginalName('');
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 280,
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 16px 44px rgba(15, 23, 42, 0.14)',
              border: '1px solid #e5e7eb',
            },
          },
        }}
      >
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={editCategoryName}
          onChange={(e) => setEditCategoryName(e.target.value)}
          placeholder={T.translate('generic.typeCategoryName') as string}
          error={editCategoryName.length > 50}
          helperText={editCategoryName.length > 50 ? T.translate('generic.nameCharLimit') : ''}
          onKeyDown={(e) => {
            e.stopPropagation();
            if (
              e.key === 'Enter' &&
              editCategoryName.trim() &&
              editCategoryName.length <= 50 &&
              editCategoryName.trim() !== editCategoryOriginalName
            ) {
              e.preventDefault();
              handleUpdateCategory();
            }
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              fontSize: '12px',
              fontWeight: 600,
              borderRadius: '5px',
              backgroundColor: '#ffffff',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#d1d5db',
              borderWidth: '1px',
            },
            '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#999999',
              borderWidth: '1px',
            },
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#999999',
              borderWidth: '1px',
            },
          }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            size="small"
            onClick={() => {
              setEditCategoryMenuAnchorEl(null);
              setEditingCategoryId(null);
              setEditCategoryName('');
              setEditCategoryOriginalName('');
            }}
          >
            {T.translate('generic.cancel')}
          </Button>
          <Button
            size="small"
            variant="contained"
            disabled={
              !editCategoryName.trim() ||
              editCategoryName.length > 50 ||
              editCategoryName.trim() === editCategoryOriginalName ||
              isUpdatingCategory
            }
            onClick={handleUpdateCategory}
          >
            {isUpdatingCategory ? (
              <CircularProgress size={16} sx={{ color: '#fff', mr: 0.5 }} />
            ) : null}
            {T.translate('generic.updateCategory')}
          </Button>
        </Box>
      </Menu>

      {/* Delete category confirmation dialog */}
      <Dialog
        open={!!deleteCategoryConfirm}
        onClose={() => setDeleteCategoryConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 700 }}>
          {T.translate('generic.deleteCategoryTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '13px' }}>
            {T.translate('generic.confirmDeleteCategory', {
              name: deleteCategoryConfirm?.name || '',
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ padding: 2 }}>
          <Button size="small" onClick={() => setDeleteCategoryConfirm(null)}>
            {T.translate('generic.cancel')}
          </Button>
          <Button size="small" variant="contained" onClick={handleConfirmDeleteCategory}>
            {T.translate('generic.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
